import React, { useState, useEffect } from 'react';
import { SeoReport, KeywordResult, CompetitorAnalysis, ContentIdea, User, PlanType } from './types';
import { analyzeContent, analyzeContentProgressive, generateKeywords, analyzeCompetitor, generateContentIdeas, AuditProgress } from './services/aiService';
import { ClientSideExtractor, InstantMetrics } from './services/clientExtractor';
import { authService } from './services/authService';
import { errorTracker } from './services/errorTracker';
import { LandingPage } from './components/LandingPage';
import { AdminPanel } from './components/AdminPanel';
import { InputForm } from './components/InputForm';
import { ResultsDashboard } from './components/ResultsDashboard';
import ProgressiveAuditDisplay from './components/ProgressiveAuditDisplay';
import { LanguageSelectionModal } from './components/LanguageSelectionModal';
import { EmailVerificationModal } from './components/EmailVerificationModal';
import { PayPalHostedButton } from './components/PayPalHostedButton';
import { Profile } from './components/Profile';
import { SEOGuide } from './components/SEOGuide';
import { AITools } from './components/AITools';
import { Reporting } from './components/Reporting';
import { SeoStrategyPlanner } from './components/SeoStrategyPlanner';
import { DesignMindAI } from './components/DesignMindAI';
import { SEOResources } from './components/SEOResources';
import { BacklinkBuilder } from './components/BacklinkBuilder';
import { GSCAnalyzer } from './components/GSCAnalyzer';
import { SpeedOptimizer } from './components/SpeedOptimizer';
import ClientBooking from './components/ClientBooking';
import AdminConsultingDashboard from './components/AdminConsultingDashboard';
import { ToolLocked } from './components/ToolLocked';
import { LoadingScreen } from './components/LoadingScreen';
import ErrorLogViewer from './components/ErrorLogViewer';
import RankTrackingDashboard from './components/RankTrackingDashboard';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { TermsOfService } from './components/TermsOfService';
import { RefundPolicy } from './components/RefundPolicy';
import { ShippingPolicy } from './components/ShippingPolicy';
import { ContactPage } from './components/ContactPage';
import { ServicesPage } from './components/ServicesPage';
import { AdvantagesPage } from './components/AdvantagesPage';
import { HowToUsePage } from './components/HowToUsePage';
import { FAQPage } from './components/FAQPage';
import { NotFoundPage } from './components/NotFoundPage';
import { CommonHeader } from './components/CommonHeader';
import { CommonFooter } from './components/CommonFooter';
import SerpRankTracker from './components/SerpRankTracker';
import JsonLdAnalyzer from './components/JsonLdAnalyzer';
// Import i18n functions with error handling
import { t, initI18n, getLanguageCode, setLanguage, getCurrentLanguage } from './services/i18n';
import { 
  BarChart3, ShieldCheck, Zap, RefreshCw, Instagram, Settings, 
  X, Lock, AlertCircle, Server, Search, 
  Swords, PenTool, Calculator, Menu, ExternalLink, Globe, MapPin, Target, Filter,
  LogOut, User as UserIcon, CreditCard, LayoutGrid, BookOpen, Lightbulb, User as UserIconLucide, FileText,
  TrendingUp, Activity, Clock, ArrowRight, Sparkles, CheckCircle, CheckCircle2, Star, Rocket, Package, Link2, ClipboardList, Calendar, Code, BarChart2, FileCode
} from 'lucide-react';

// --- SHARED TYPES ---
interface ProjectConfig {
  country: string;
  language: string;
}

// --- SUB-COMPONENTS FOR TOOLS (Reusing logic but wrapped) ---

// 1. KEYWORD TOOL COMPONENT
interface KeywordToolProps {
  config: ProjectConfig;
  onRun: (seed: string, intent: string) => Promise<KeywordResult[] | undefined>;
  results: KeywordResult[] | null;
  loading: boolean;
}

const KeywordTool: React.FC<KeywordToolProps> = ({ config, onRun, results, loading }) => {
  const [seed, setSeed] = useState('');
  const [intentFilter, setIntentFilter] = useState('All');

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if(!seed) return;
    await onRun(seed, intentFilter);
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 p-4 opacity-10">
           <Search size={100} />
        </div>
        <div className="relative z-10">
          <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
            <Search className="text-indigo-400"/> Keyword Genius
          </h2>
          <div className="flex items-center gap-2 text-xs text-slate-400 mb-6 bg-slate-800/50 w-fit px-3 py-1 rounded-full border border-slate-700">
            <MapPin size={12} className="text-emerald-400"/> {config.country}
            <span className="text-slate-600">|</span>
            <Globe size={12} className="text-blue-400"/> {config.language}
          </div>

          <form onSubmit={handleSearch} className="space-y-4">
             <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <input 
                    type="text" 
                    value={seed} 
                    onChange={(e) => setSeed(e.target.value)}
                    placeholder="Seed Keyword (Ex: Immobilier, Vente voiture...)" 
                    className="w-full bg-slate-800 border-slate-700 text-white rounded-xl pl-4 pr-4 py-3 focus:ring-2 focus:ring-indigo-500 outline-none"
                  />
                </div>
                
                <div className="w-full md:w-48 relative">
                   <select 
                     value={intentFilter}
                     onChange={(e) => setIntentFilter(e.target.value)}
                     className="w-full bg-slate-800 border-slate-700 text-slate-300 rounded-xl pl-4 pr-4 py-3 focus:ring-2 focus:ring-indigo-500 outline-none appearance-none cursor-pointer"
                   >
                     <option value="All">All Intents</option>
                     <option value="Informational">Informational</option>
                     <option value="Transactional">Transactional</option>
                     <option value="Commercial">Commercial</option>
                     <option value="Navigational">Navigational</option>
                   </select>
                </div>

                <button disabled={loading} className="bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-3 rounded-xl font-bold transition-all disabled:opacity-50 flex items-center gap-2 justify-center shadow-lg shadow-indigo-600/20">
                  {loading ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"/> : <Search size={18}/>}
                  {loading ? 'Thinking...' : 'Chercher'}
                </button>
             </div>
          </form>
        </div>
      </div>

      {results && (
        <div className="animate-slideUp">
          <div className="grid gap-3">
            {results.map((kw, idx) => (
              <div key={idx} className="bg-white group p-4 rounded-xl shadow-sm border border-slate-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 hover:border-indigo-500 hover:shadow-md transition-all duration-300" dir="ltr">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-bold text-slate-800 group-hover:text-indigo-600 transition-colors">{kw.keyword}</h3>
                    <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wide ${kw.difficulty === 'Hard' ? 'bg-red-100 text-red-600' : kw.difficulty === 'Medium' ? 'bg-orange-100 text-orange-600' : 'bg-emerald-100 text-emerald-600'}`}>
                      {kw.difficulty}
                    </span>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <span className="text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded font-bold uppercase border border-blue-100 flex items-center gap-1">
                      <Target size={10} /> {kw.intent}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-8 w-full md:w-auto border-t md:border-t-0 border-slate-100 pt-3 md:pt-0">
                  <div className="text-center min-w-[80px]">
                    <span className="block font-bold text-slate-800 text-lg">{kw.volumeEstimate}</span>
                    <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Volume</span>
                  </div>
                  <div className="w-px h-8 bg-slate-200 hidden md:block"></div>
                  <div className="text-center min-w-[80px]">
                    <span className="block font-bold text-slate-800 text-lg">{kw.cpcEstimate}</span>
                    <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">CPC (Est.)</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// 2. COMPETITOR TOOL (Reused logic)
interface CompetitorToolProps {
  onRun: (myUrl: string, compUrl: string) => Promise<CompetitorAnalysis | undefined>;
  analysis: CompetitorAnalysis | null;
  loading: boolean;
}
const CompetitorTool: React.FC<CompetitorToolProps> = ({ onRun, analysis, loading }) => {
  const [myUrl, setMyUrl] = useState('');
  const [compUrl, setCompUrl] = useState('');

  const handleCompare = async (e: React.FormEvent) => {
    e.preventDefault();
    if(!myUrl || !compUrl) return;
    await onRun(myUrl, compUrl);
  };

  return (
    <div className="space-y-6 animate-fadeIn">
       <div className="bg-white/5 border border-white/10 rounded-2xl p-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 p-4 opacity-10"><Swords size={100} /></div>
        <div className="relative z-10">
          <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2"><Swords className="text-red-400"/> {t('competitor.title') || 'Competitor Spy'}</h2>
          <form onSubmit={handleCompare} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <input type="url" value={myUrl} onChange={(e) => setMyUrl(e.target.value)} placeholder={t('competitor.myUrlPlaceholder') || 'My URL (https://...)'} className="bg-slate-800 border-slate-700 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 outline-none w-full" />
              <input type="url" value={compUrl} onChange={(e) => setCompUrl(e.target.value)} placeholder={t('competitor.competitorUrlPlaceholder') || 'Competitor URL (https://...)'} className="bg-slate-800 border-slate-700 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-red-500 outline-none w-full" />
            </div>
            <button disabled={loading} className="w-full bg-red-600 hover:bg-red-500 text-white px-6 py-3 rounded-xl font-bold transition-all disabled:opacity-50 shadow-lg shadow-red-600/20">
              {loading ? (t('competitor.analyzing') || 'Analyzing Fight...') : (t('competitor.compareButton') || 'Compare Sites')}
            </button>
          </form>
        </div>
      </div>
      {analysis && (
        <div className="space-y-6" dir="ltr">
          {/* Extracted Data - Basic Info */}
          {analysis.extractedData?.basicInfo && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <FileText className="text-blue-400" size={20} />
                {t('competitor.basicInfo') || 'Basic Information'}
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-400">{t('competitor.title') || 'Title'}:</span>
                  <span className="text-white ml-2">{analysis.extractedData.basicInfo.title}</span>
                  <span className="text-slate-500 ml-2">({analysis.extractedData.basicInfo.titleLength} chars)</span>
          </div>
                <div>
                  <span className="text-slate-400">{t('competitor.metaDescription') || 'Meta Description'}:</span>
                  <span className="text-white ml-2">{analysis.extractedData.basicInfo.metaDescription.substring(0, 100)}...</span>
                  <span className="text-slate-500 ml-2">({analysis.extractedData.basicInfo.metaDescriptionLength} chars)</span>
                </div>
                <div>
                  <span className="text-slate-400">H1:</span>
                  <span className="text-white ml-2">{analysis.extractedData.basicInfo.h1}</span>
                  <span className="text-slate-500 ml-2">({analysis.extractedData.basicInfo.h1Count} found)</span>
                </div>
                <div>
                  <span className="text-slate-400">{t('competitor.language') || 'Language'}:</span>
                  <span className="text-white ml-2">{analysis.extractedData.basicInfo.language}</span>
                </div>
                <div>
                  <span className="text-slate-400">SSL:</span>
                  <span className={`ml-2 ${analysis.extractedData.technicalSEO?.hasSSL ? 'text-green-400' : 'text-red-400'}`}>
                    {analysis.extractedData.technicalSEO?.hasSSL ? '✓ Yes' : '✗ No'}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400">Canonical:</span>
                  <span className={`ml-2 ${analysis.extractedData.technicalSEO?.hasCanonical ? 'text-green-400' : 'text-red-400'}`}>
                    {analysis.extractedData.technicalSEO?.hasCanonical ? '✓ Yes' : '✗ No'}
                  </span>
                </div>
          </div>
            </div>
          )}

          {/* Content Analysis */}
          {analysis.extractedData?.contentAnalysis && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <FileText className="text-purple-400" size={20} />
                {t('competitor.contentAnalysis') || 'Content Analysis'}
              </h3>
              <div className="grid md:grid-cols-4 gap-4 mb-4">
                <div className="bg-slate-900/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-400">{analysis.extractedData.contentAnalysis.wordCount.toLocaleString()}</div>
                  <div className="text-xs text-slate-400 mt-1">{t('competitor.wordCount') || 'Words'}</div>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-400">{analysis.extractedData.contentAnalysis.headingCounts.h2}</div>
                  <div className="text-xs text-slate-400 mt-1">H2 Tags</div>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-400">{analysis.extractedData.contentAnalysis.images.withAlt}</div>
                  <div className="text-xs text-slate-400 mt-1">{t('competitor.imagesWithAlt') || 'Images w/ Alt'}</div>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-yellow-400">{analysis.extractedData.contentAnalysis.links.internal}</div>
                  <div className="text-xs text-slate-400 mt-1">{t('competitor.internalLinks') || 'Internal Links'}</div>
                </div>
              </div>
              
              {/* Top Keywords */}
              {analysis.extractedData.contentAnalysis.topKeywords && analysis.extractedData.contentAnalysis.topKeywords.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-semibold text-slate-300 mb-2">{t('competitor.topKeywords') || 'Top Keywords'}:</h4>
                  <div className="flex flex-wrap gap-2">
                    {analysis.extractedData.contentAnalysis.topKeywords.slice(0, 15).map((kw: any, i: number) => (
                      <span key={i} className="px-2 py-1 bg-indigo-500/20 text-indigo-300 rounded text-xs">
                        {kw.word} ({kw.count}x, {kw.percentage})
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Technical SEO */}
          {analysis.extractedData?.technicalSEO && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Settings className="text-green-400" size={20} />
                {t('competitor.technicalSEO') || 'Technical SEO'}
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className={`p-3 rounded-lg ${analysis.extractedData.technicalSEO.hasSSL ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                  <div className="text-sm font-semibold text-white">SSL/HTTPS</div>
                  <div className={`text-xs mt-1 ${analysis.extractedData.technicalSEO.hasSSL ? 'text-green-400' : 'text-red-400'}`}>
                    {analysis.extractedData.technicalSEO.hasSSL ? '✓ Enabled' : '✗ Not Enabled'}
                  </div>
                </div>
                <div className={`p-3 rounded-lg ${analysis.extractedData.technicalSEO.structuredData.count > 0 ? 'bg-green-500/10 border border-green-500/30' : 'bg-yellow-500/10 border border-yellow-500/30'}`}>
                  <div className="text-sm font-semibold text-white">Structured Data</div>
                  <div className="text-xs mt-1 text-slate-300">{analysis.extractedData.technicalSEO.structuredData.count} schemas</div>
                </div>
                <div className={`p-3 rounded-lg ${analysis.extractedData.technicalSEO.xmlSitemap.exists ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                  <div className="text-sm font-semibold text-white">XML Sitemap</div>
                  <div className={`text-xs mt-1 ${analysis.extractedData.technicalSEO.xmlSitemap.exists ? 'text-green-400' : 'text-red-400'}`}>
                    {analysis.extractedData.technicalSEO.xmlSitemap.exists ? '✓ Found' : '✗ Not Found'}
                  </div>
                </div>
                <div className={`p-3 rounded-lg ${analysis.extractedData.technicalSEO.openGraph.hasTitle && analysis.extractedData.technicalSEO.openGraph.hasImage ? 'bg-green-500/10 border border-green-500/30' : 'bg-yellow-500/10 border border-yellow-500/30'}`}>
                  <div className="text-sm font-semibold text-white">Open Graph</div>
                  <div className="text-xs mt-1 text-slate-300">
                    {analysis.extractedData.technicalSEO.openGraph.hasTitle && analysis.extractedData.technicalSEO.openGraph.hasImage ? '✓ Complete' : '⚠ Incomplete'}
                  </div>
                </div>
                <div className={`p-3 rounded-lg ${analysis.extractedData.technicalSEO.hasViewport ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                  <div className="text-sm font-semibold text-white">Mobile Viewport</div>
                  <div className={`text-xs mt-1 ${analysis.extractedData.technicalSEO.hasViewport ? 'text-green-400' : 'text-red-400'}`}>
                    {analysis.extractedData.technicalSEO.hasViewport ? '✓ Configured' : '✗ Missing'}
                  </div>
                </div>
                <div className={`p-3 rounded-lg ${analysis.extractedData.technicalSEO.robotsTxt.exists ? 'bg-green-500/10 border border-green-500/30' : 'bg-yellow-500/10 border border-yellow-500/30'}`}>
                  <div className="text-sm font-semibold text-white">Robots.txt</div>
                  <div className={`text-xs mt-1 ${analysis.extractedData.technicalSEO.robotsTxt.exists ? 'text-green-400' : 'text-yellow-400'}`}>
                    {analysis.extractedData.technicalSEO.robotsTxt.exists ? '✓ Found' : '⚠ Not Found'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Technology Stack */}
          {analysis.extractedData?.technologyStack && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Code className="text-orange-400" size={20} />
                {t('competitor.technologyStack') || 'Technology Stack'}
              </h3>
              <div className="space-y-3">
                {analysis.extractedData.technologyStack.cms && analysis.extractedData.technologyStack.cms.length > 0 && (
                  <div>
                    <span className="text-sm font-semibold text-slate-300">CMS:</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {analysis.extractedData.technologyStack.cms.map((tech: string, i: number) => (
                        <span key={i} className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs">{tech}</span>
                      ))}
                    </div>
                  </div>
                )}
                {analysis.extractedData.technologyStack.frameworks && analysis.extractedData.technologyStack.frameworks.length > 0 && (
                  <div>
                    <span className="text-sm font-semibold text-slate-300">Frameworks:</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {analysis.extractedData.technologyStack.frameworks.map((tech: string, i: number) => (
                        <span key={i} className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded text-xs">{tech}</span>
                      ))}
                    </div>
                  </div>
                )}
                {analysis.extractedData.technologyStack.analytics && analysis.extractedData.technologyStack.analytics.length > 0 && (
                  <div>
                    <span className="text-sm font-semibold text-slate-300">Analytics:</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {analysis.extractedData.technologyStack.analytics.map((tech: string, i: number) => (
                        <span key={i} className="px-2 py-1 bg-green-500/20 text-green-300 rounded text-xs">{tech}</span>
                      ))}
                    </div>
                  </div>
                )}
                {analysis.extractedData.technologyStack.ecommerce && analysis.extractedData.technologyStack.ecommerce.length > 0 && (
                  <div>
                    <span className="text-sm font-semibold text-slate-300">E-commerce:</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {analysis.extractedData.technologyStack.ecommerce.map((tech: string, i: number) => (
                        <span key={i} className="px-2 py-1 bg-yellow-500/20 text-yellow-300 rounded text-xs">{tech}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Strongest Features Section */}
          {analysis.strongestFeatures && (
            <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border-2 border-red-500/30 rounded-xl p-6">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="text-red-400" size={24} />
                {t('competitor.strongestFeatures') || 'Competitor\'s Strongest Features'}
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {analysis.strongestFeatures.content && (
                  <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                    <div className="text-sm font-semibold text-indigo-400 mb-2">Content Strengths</div>
                    <p className="text-slate-300 text-sm">{analysis.strongestFeatures.content}</p>
                  </div>
                )}
                {analysis.strongestFeatures.technical && (
                  <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                    <div className="text-sm font-semibold text-indigo-400 mb-2">Technical SEO</div>
                    <p className="text-slate-300 text-sm">{analysis.strongestFeatures.technical}</p>
                  </div>
                )}
                {analysis.strongestFeatures.keywords && (
                  <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                    <div className="text-sm font-semibold text-indigo-400 mb-2">Keyword Strategy</div>
                    <p className="text-slate-300 text-sm">{analysis.strongestFeatures.keywords}</p>
                  </div>
                )}
                {analysis.strongestFeatures.structure && (
                  <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                    <div className="text-sm font-semibold text-indigo-400 mb-2">Content Structure</div>
                    <p className="text-slate-300 text-sm">{analysis.strongestFeatures.structure}</p>
                  </div>
                )}
                {analysis.strongestFeatures.backlinks && (
                  <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                    <div className="text-sm font-semibold text-indigo-400 mb-2">Backlink Profile</div>
                    <p className="text-slate-300 text-sm">{analysis.strongestFeatures.backlinks}</p>
                  </div>
                )}
                {analysis.strongestFeatures.aeo && (
                  <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                    <div className="text-sm font-semibold text-indigo-400 mb-2">AEO Optimization</div>
                    <p className="text-slate-300 text-sm">{analysis.strongestFeatures.aeo}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Content Gap */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <AlertCircle className="text-red-400" size={20} />
              {t('competitor.contentGap') || 'Content Gap'}
            </h3>
            <ul className="space-y-3">
              {analysis.contentGap.map((gap, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-300 bg-slate-800/50 p-3 rounded-lg">
                  <span className="text-red-400 mt-1 font-bold">•</span>
                  <span>{typeof gap === 'string' ? gap : (gap as any)?.gap || String(gap)}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Missing Keywords */}
          {analysis.missingKeywords && analysis.missingKeywords.length > 0 && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Zap className="text-yellow-400" size={20} />
                {t('competitor.missingKeywords') || 'Missing Keywords'}
              </h3>
              <div className="flex flex-wrap gap-2">
                {analysis.missingKeywords.map((keyword, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded-lg text-sm font-medium"
                  >
                    {typeof keyword === 'string' ? keyword : (keyword as any)?.keyword || String(keyword)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Technical Advantages */}
          {analysis.technicalAdvantages && analysis.technicalAdvantages.length > 0 && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Settings className="text-blue-400" size={20} />
                {t('competitor.technicalAdvantages') || 'Technical Advantages'}
              </h3>
              <ul className="space-y-2">
                {analysis.technicalAdvantages.map((adv, i) => (
                  <li key={i} className="text-slate-300 text-sm bg-slate-800/50 p-3 rounded-lg">
                    {adv}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Strategy */}
          <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border-2 border-indigo-500/30 rounded-xl p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Target className="text-indigo-400" size={20} />
              {t('competitor.strategy') || 'Actionable Strategy'}
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">
              {typeof analysis.strategy === 'string' ? analysis.strategy : (analysis.strategy as any)?.summary || JSON.stringify(analysis.strategy)}
            </p>
          </div>

          {/* Detailed Report */}
          {analysis.detailedReport && (
            <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <FileText className="text-emerald-400" size={20} />
                {t('competitor.detailedReport') || 'Detailed Analysis Report'}
              </h3>
              <div className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap bg-slate-800/50 p-4 rounded-lg">
                {analysis.detailedReport}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Helper to get ROI explanation text based on language
const getRoiExplanation = (language: string) => {
  switch (language) {
    case 'Arabic':
    case 'العربية':
      return (
        <p className="text-slate-300 text-sm leading-relaxed">
          هاد المحاكي كيعطيك فكرة <span className="font-semibold text-white">شحال من دخل</span> يقدر يجيب ليك
          الترافيك ديال الموقع ديالك. <span className="font-semibold text-white">الترافيك الشهري</span> هو عدد
          الزوار، <span className="font-semibold text-white">نسبة التحويل</span> هي النسبة ديال الناس اللي
          كيتحوّلو لعملاء محتملين (leads)، و
          <span className="font-semibold text-white"> قيمة كل lead</span> هي شحال كيتساوى عليك كل زبون فالمتوسط.
          الأداة كتحسب <span className="font-semibold text-white">عدد الـ leads فالشهر</span> و
          <span className="font-semibold text-white"> الدخل الشهري التقريبي</span> (leads × قيمة كل lead) باش
          تشوف الإمكانات الحقيقية ديال الـ SEO ديالك.
        </p>
      );
    case 'Darija/French':
    case 'Darija':
      return (
        <p className="text-slate-300 text-sm leading-relaxed">
          Hna katsouf <span className="font-semibold text-white">ch7al dyal trafic</span> kayji lel site,
          <span className="font-semibold text-white"> conv. rate</span> hiya pourcentage dyal nass li kayt9albou l&apos;clients,
          w <span className="font-semibold text-white">valeur / lead (€)</span> hiya ch7al katswa kliyan wa7ed.
          L&apos;outil kay7seb lik <span className="font-semibold text-white">ch7al dyal leads</span> momkin tjib men had l&apos;trafic,
          w <span className="font-semibold text-white">revenu mensuel estimé</span> = leads × valeur par lead.
          Hadchi kay3tik idée wach l&apos;SEO ou l&apos;campagne dyalek rentable (ROI) ola la.
        </p>
      );
    case 'Deutsch':
    case 'German':
      return (
        <p className="text-slate-300 text-sm leading-relaxed">
          Dieser Simulator schätzt, <span className="font-semibold text-white">wie viel Umsatz</span> dein
          Traffic generieren kann. <span className="font-semibold text-white">Monatlicher Traffic</span> ist die
          Anzahl der Besucher, <span className="font-semibold text-white">Conversion‑Rate</span> ist der Prozentsatz,
          der zu Leads wird, und
          <span className="font-semibold text-white"> Wert pro Lead</span> ist, wie viel dir ein Lead im
          Durchschnitt einbringt. Das Tool berechnet
          <span className="font-semibold text-white"> Leads pro Monat</span> und deinen
          <span className="font-semibold text-white"> geschätzten Monatsumsatz</span> (Leads × Wert pro Lead), damit
          du das mögliche ROI‑Potenzial deiner SEO siehst.
        </p>
      );
    case 'French':
      return (
        <p className="text-slate-300 text-sm leading-relaxed">
          Ici tu vois <span className="font-semibold text-white">le trafic mensuel</span> estimé sur ton site,
          le <span className="font-semibold text-white">taux de conversion</span> (pourcentage de visiteurs qui deviennent des leads),
          et la <span className="font-semibold text-white">valeur moyenne par lead</span> en euros.
          L&apos;outil calcule <span className="font-semibold text-white">le nombre de leads par mois</span> et
          le <span className="font-semibold text-white">revenu mensuel estimé</span> (leads × valeur par lead).
          Ça te permet de comprendre combien peut rapporter ton SEO si tu augmentes le trafic ou le taux de conversion.
        </p>
      );
    case 'Spanish':
    case 'Español':
      return (
        <p className="text-slate-300 text-sm leading-relaxed">
          Este simulador estima <span className="font-semibold text-white">cuánto ingreso</span> puede generar tu
          tráfico. <span className="font-semibold text-white">Tráfico mensual</span> es cuántos visitantes llegan a
          tu sitio, <span className="font-semibold text-white">tasa de conversión</span> es el porcentaje que se
          convierte en leads y
          <span className="font-semibold text-white"> valor por lead</span> es cuánto vale de media cada lead.
          La herramienta calcula <span className="font-semibold text-white">leads por mes</span> y tu
          <span className="font-semibold text-white"> ingreso mensual estimado</span> (leads × valor por lead) para
          que veas el ROI potencial de tu SEO.
        </p>
      );
    default:
      return (
        <p className="text-slate-300 text-sm leading-relaxed">
          This simulator estimates how much revenue your traffic can generate.
          <span className="font-semibold text-white"> Monthly traffic</span> is how many visitors you get,
          <span className="font-semibold text-white"> conversion rate</span> is the percentage of visitors that turn into leads,
          and <span className="font-semibold text-white"> value per lead</span> is how much one lead is worth on average.
          The tool calculates <span className="font-semibold text-white">leads per month</span> and your
          <span className="font-semibold text-white"> estimated monthly revenue</span> (leads × value per lead) so you can see the potential ROI of your SEO.
        </p>
      );
  }
};

// 4. ROI CALCULATOR (Static)
interface RoiCalculatorProps {
  language: string;
}

const RoiCalculator: React.FC<RoiCalculatorProps> = ({ language }) => {
  const [traffic, setTraffic] = useState(1000);
  const [convRate, setConvRate] = useState(2);
  const [valuePerLead, setValuePerLead] = useState(100);
  const leads = Math.floor(traffic * (convRate / 100));
  const revenue = leads * valuePerLead;

  return (
    <div className="space-y-6 animate-fadeIn">
       <div className="bg-white/5 border border-white/10 rounded-2xl p-6 relative overflow-hidden">
        <div className="relative z-10">
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <Calculator className="text-blue-400"/> {t('roi.title') || 'ROI Simulator'}
          </h2>
          <div className="mb-4">
            {getRoiExplanation(language)}
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-4">
            <div>
              <label className="text-slate-300 text-xs font-bold uppercase mb-2 block">{t('roi.monthlyTraffic') || 'Monthly Traffic'}</label>
              <input
                type="number"
                value={traffic}
                onChange={e => setTraffic(Number(e.target.value))}
                className="w-full bg-slate-800 text-white p-3 rounded-lg border border-slate-600"
              />
            </div>
            <div>
              <label className="text-slate-300 text-xs font-bold uppercase mb-2 block">{t('roi.conversionRate') || 'Conv. Rate (%)'}</label>
              <input
                type="number"
                value={convRate}
                onChange={e => setConvRate(Number(e.target.value))}
                className="w-full bg-slate-800 text-white p-3 rounded-lg border border-slate-600"
              />
            </div>
            <div>
              <label className="text-slate-300 text-xs font-bold uppercase mb-2 block">{t('roi.valuePerLead') || 'Value / Lead (€)'}</label>
              <input
                type="number"
                value={valuePerLead}
                onChange={e => setValuePerLead(Number(e.target.value))}
                className="w-full bg-slate-800 text-white p-3 rounded-lg border border-slate-600"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-6" dir="ltr">
         <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-8 rounded-2xl text-white shadow-xl relative overflow-hidden">
          <span className="block text-blue-200 font-bold text-sm uppercase mb-1">{t('roi.estimatedRevenue') || 'Estimated Monthly Revenue'}</span>
          <span className="block text-5xl font-extrabold tracking-tight">
            {revenue.toLocaleString()} €
          </span>
          <div className="mt-4 pt-4 border-t border-white/20 flex justify-between items-center">
            <span>{t('roi.leadsPerMonth') || 'Leads / Month'}</span>
            <span className="font-bold text-xl">{leads}</span>
          </div>
         </div>
      </div>
    </div>
  );
};


// --- MAIN APP COMPONENT ---

const App: React.FC = () => {
  // Log that App component is rendering
  console.log('App component rendering...');
  
  // --- AUTH STATE ---
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authView, setAuthView] = useState<'login' | 'register' | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [authError, setAuthError] = useState('');

  // --- APP STATE ---
  type ViewType =
    | 'landing'
    | 'dashboard'
    | 'tools'
    | 'admin'
    | 'profile'
    | 'seo-guide'
    | 'ai-tools'
    | 'reporting'
    | 'strategy'
    | 'rank-tracking'
    | 'jsonld'
    | 'gsc-analyzer'
    | 'speed-optimizer'
    | 'designmind'
    | 'seo-resources'
    | 'backlinks'
    | 'consulting'
    | 'privacy-policy'
    | 'terms-of-service'
    | 'refund-policy'
    | 'shipping-policy'
    | 'contact'
    | 'services'
    | 'advantages'
    | 'how-to-use'
    | 'faq'
    | '404';
  const [view, setView] = useState<ViewType>('landing');
  const [activeModule, setActiveModule] = useState<'audit' | 'keywords' | 'competitors' | 'roi'>('audit');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showErrorLogViewer, setShowErrorLogViewer] = useState(false);
  const [showLanguageModalOnLanding, setShowLanguageModalOnLanding] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState<string>('');
  const [showRankTrackingModal, setShowRankTrackingModal] = useState(false);
  const [rankTrackingModalData, setRankTrackingModalData] = useState({
    projectName: '',
    domain: '',
    trackingFrequency: 'daily' as 'daily' | 'weekly' | 'monthly'
  });
  const [rankTrackingRefreshKey, setRankTrackingRefreshKey] = useState(0);
  const [projectConfig, setProjectConfig] = useState<ProjectConfig>({ country: 'United States', language: 'English' });
  const [uiLanguage, setUiLanguage] = useState<string>('English');

  // --- TOOL STATE --- (Separate states for each tool to prevent interference)
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  
  // Separate loading states for each tool
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditLoadingMessage, setAuditLoadingMessage] = useState('');
  const [auditProgress, setAuditProgress] = useState<Map<string, AuditProgress>>(new Map());
  const [partialReport, setPartialReport] = useState<SeoReport | null>(null);
  const [keywordsLoading, setKeywordsLoading] = useState(false);
  const [competitorLoading, setCompetitorLoading] = useState(false);
  const [speedOptimizerLoading, setSpeedOptimizerLoading] = useState(false);
  const [gscLoading, setGscLoading] = useState(false);
  
  // Tool results with persistence
  const [report, setReport] = useState<SeoReport | null>(() => {
    try {
      const saved = localStorage.getItem('orca_audit_report');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [keywordResults, setKeywordResults] = useState<KeywordResult[] | null>(() => {
    try {
      const saved = localStorage.getItem('orca_keyword_results');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [compAnalysis, setCompAnalysis] = useState<CompetitorAnalysis | null>(() => {
    try {
      const saved = localStorage.getItem('orca_competitor_analysis');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [speedOptimizerResults, setSpeedOptimizerResults] = useState<any>(() => {
    try {
      const saved = localStorage.getItem('orca_speed_optimizer_results');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [gscAnalysis, setGscAnalysis] = useState<any>(() => {
    try {
      const saved = localStorage.getItem('orca_gsc_analysis');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  
  const [isInitialized, setIsInitialized] = useState(false);
  const [isAppLoading, setIsAppLoading] = useState(true);
  const [forceShow, setForceShow] = useState(false);
  
  // Persist results to localStorage whenever they change
  useEffect(() => {
    if (report) {
      localStorage.setItem('orca_audit_report', JSON.stringify(report));
    }
  }, [report]);
  
  useEffect(() => {
    if (keywordResults) {
      localStorage.setItem('orca_keyword_results', JSON.stringify(keywordResults));
    }
  }, [keywordResults]);
  
  useEffect(() => {
    if (compAnalysis) {
      localStorage.setItem('orca_competitor_analysis', JSON.stringify(compAnalysis));
    }
  }, [compAnalysis]);
  
  useEffect(() => {
    if (speedOptimizerResults) {
      localStorage.setItem('orca_speed_optimizer_results', JSON.stringify(speedOptimizerResults));
    }
  }, [speedOptimizerResults]);
  
  useEffect(() => {
    if (gscAnalysis) {
      localStorage.setItem('orca_gsc_analysis', JSON.stringify(gscAnalysis));
    }
  }, [gscAnalysis]);
  
  // --- BACKGROUND TASK MANAGEMENT ---
  interface BackgroundTask {
    id: string;
    type: 'audit' | 'keywords' | 'competitor' | 'ai-tool';
    status: 'running' | 'completed' | 'failed';
    message: string;
    result?: any;
    error?: string;
    startTime: number;
  }
  
  const [backgroundTasks, setBackgroundTasks] = useState<Map<string, BackgroundTask>>(new Map());
  const [taskNotifications, setTaskNotifications] = useState<Array<{id: string; message: string; type: 'success' | 'error'}>>([]);
  
  // Auto-remove notifications after 5 seconds
  useEffect(() => {
    if (taskNotifications.length > 0) {
      const timer = setTimeout(() => {
        setTaskNotifications(prev => prev.slice(1));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [taskNotifications]);

  // INITIALIZATION
  // Listen for session conflict events from API layer
  useEffect(() => {
    const handleSessionConflict = (event: CustomEvent) => {
      console.log('Session conflict detected:', event.detail);
      setCurrentUser(null);
      setView('landing');
      // Show alert to user
      alert(event.detail.message || 'Your account is logged in on another device or browser. Please log in again to continue.');
    };

    window.addEventListener('session-conflict', handleSessionConflict as EventListener);
    return () => {
      window.removeEventListener('session-conflict', handleSessionConflict as EventListener);
    };
  }, []);

  useEffect(() => {
    const initUser = async () => {
      try {
        // Check URL parameter for language first (priority over localStorage)
        const urlParams = new URLSearchParams(window.location.search);
        const urlLang = urlParams.get('lang');
        
        // Map URL lang parameter to language name
        let initialLanguage = localStorage.getItem('orca_ui_language') || 'English';
        if (urlLang) {
          const langMap: Record<string, string> = {
            'fr': 'French',
            'en': 'English',
            'ar': 'Arabic',
            'darija': 'Darija',
            'de': 'German',
            'es': 'Spanish'
          };
          const mappedLang = langMap[urlLang.toLowerCase()];
          if (mappedLang) {
            initialLanguage = mappedLang;
            // Save to localStorage for future visits
            localStorage.setItem('orca_ui_language', mappedLang);
            localStorage.setItem('orca_ui_language_code', urlLang.toLowerCase());
            // Clean URL (remove lang parameter after reading it)
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('lang');
            window.history.replaceState({}, '', newUrl.pathname + newUrl.search);
          }
        }
        
        // Initialize i18n first (non-blocking, has fallback)
        setUiLanguage(initialLanguage);
        const langCode = getLanguageCode(initialLanguage);
        // Don't await - let it load in background
        initI18n(langCode).catch(err => {
          console.warn('i18n init failed, using defaults:', err);
        });
        
        // Preload common languages in background for better performance (non-blocking)
        import('./src/utils/i18nPerformance').then(({ preloadCommonLanguages }) => {
          preloadCommonLanguages().catch(() => {
            // Silently fail - this is just an optimization
          });
        }).catch(() => {
          // Silently fail if import fails - not critical
        });
        
        // Set RTL/LTR direction based on language
        try {
          const { isRTL, getTextDirection } = await import('./services/i18n');
          const direction = getTextDirection(langCode);
          document.documentElement.setAttribute('dir', direction);
          document.documentElement.setAttribute('lang', langCode);
          
          // Ensure header stays visible in RTL - use setTimeout to ensure DOM is ready
          setTimeout(() => {
            const header = document.querySelector('header.sticky');
            if (header) {
              const headerEl = header as HTMLElement;
              headerEl.style.position = 'sticky';
              headerEl.style.top = '0';
              headerEl.style.left = '0';
              headerEl.style.right = '0';
              headerEl.style.zIndex = '30';
              headerEl.style.width = '100%';
              headerEl.style.display = 'flex';
            }
          }, 100);
        } catch (dirError) {
          console.warn('Failed to set text direction:', dirError);
          // Set default direction
          document.documentElement.setAttribute('dir', 'ltr');
          document.documentElement.setAttribute('lang', langCode);
        }
      } catch (initError) {
        console.warn('Initialization error (non-critical):', initError);
        // Continue with default settings
        document.documentElement.setAttribute('dir', 'ltr');
        document.documentElement.setAttribute('lang', 'en');
      }

      // Check for language preference on landing page (check after view is set)
      // Note: This is checked later in the code, not here

      // Try to get cached user first for faster initial load (needed for route detection)
      const cachedUser = authService.getCachedUser();

      // Route detection based on URL pathname (for 404 handling)
      const pathname = window.location.pathname;
      const validRoutes: Record<string, ViewType> = {
        '/': 'landing',
        '/dashboard': 'dashboard',
        '/tools': 'tools',
        '/admin': 'admin',
        '/profile': 'profile',
        '/seo-guide': 'seo-guide',
        '/ai-tools': 'ai-tools',
        '/reporting': 'reporting',
        '/strategy': 'strategy',
        '/rank-tracking': 'rank-tracking',
        '/jsonld': 'jsonld',
        '/gsc-analyzer': 'gsc-analyzer',
        '/speed-optimizer': 'speed-optimizer',
        '/designmind': 'designmind',
        '/seo-resources': 'seo-resources',
        '/backlinks': 'backlinks',
        '/consulting': 'consulting',
        '/privacy-policy': 'privacy-policy',
        '/terms-of-service': 'terms-of-service',
        '/refund-policy': 'refund-policy',
        '/shipping-policy': 'shipping-policy',
        '/contact': 'contact',
        '/services': 'services',
        '/advantages': 'advantages',
        '/how-to-use': 'how-to-use',
        '/faq': 'faq'
      };

      // If pathname doesn't match any valid route and we're not logged in, set to 404
      if (!cachedUser && pathname !== '/' && !validRoutes[pathname]) {
        // Set view to a special 404 indicator
        setView('404' as ViewType);
      } else if (validRoutes[pathname] && pathname !== '/') {
        // If we have a valid route, set the view accordingly
        setView(validRoutes[pathname]);
      }

      // Check for PayPal payment return
      const urlParams = new URLSearchParams(window.location.search);
      const paymentReturn = urlParams.get('payment_return');
      const paymentCancelled = urlParams.get('payment_cancelled');
      const token = urlParams.get('token'); // PayPal returns this

      // Check for booking payment return
      const bookingId = urlParams.get('bookingId');
      const bookingStatus = urlParams.get('status');
      // PayPal returns order ID as 'token' parameter in the redirect URL after approval
      const paypalOrderId = urlParams.get('token');

      if (bookingId && bookingStatus) {
        // Handle booking payment return
        if (bookingStatus === 'success' && paypalOrderId) {
          try {
            const { bookingService } = await import('./services/api');
            const result = await bookingService.verifyPayment(bookingId, paypalOrderId);
            if (result.success) {
              // Clean URL
              window.history.replaceState({}, '', '/consulting');
              // Show success message and refresh
              alert('Payment successful! Your booking has been confirmed. You will receive a confirmation email shortly.');
              window.location.reload();
            }
          } catch (error: any) {
            console.error('Booking payment verification error:', error);
            alert(`Payment verification failed: ${error.message || 'Unknown error'}`);
            // Clean URL even on error
            window.history.replaceState({}, '', '/consulting');
          }
        } else if (bookingStatus === 'cancelled') {
          // User cancelled booking payment
          window.history.replaceState({}, '', '/consulting');
          alert('Booking payment was cancelled.');
        }
      } else if (paymentReturn && token) {
        // Handle PayPal payment return - PayPal redirects with token
        const pendingPayment = localStorage.getItem('pending_payment');
        if (pendingPayment) {
          const { orderId, plan } = JSON.parse(pendingPayment);
          try {
            const result = await authService.capturePayPalOrder(orderId, plan);
            if (result.success) {
              setCurrentUser(result.user);
              setShowPaywall(false);
              localStorage.removeItem('pending_payment');
              // Clean URL
              window.history.replaceState({}, '', window.location.pathname);
              alert(`Welcome to OrcaTools ${plan.toUpperCase()}! Payment successful.`);
            }
          } catch (error: any) {
            alert(`Payment processing failed: ${error.message}`);
          }
        }
      } else if (paymentCancelled) {
        // User cancelled payment
        localStorage.removeItem('pending_payment');
        window.history.replaceState({}, '', window.location.pathname);
        alert('Payment was cancelled.');
      }

      // Use cached user (already retrieved above) for faster initial load
      if (cachedUser) {
        setCurrentUser(cachedUser);
        setView('dashboard');
        
        // Load cached language preferences
        if (cachedUser.preferredLanguage && cachedUser.preferredCountry) {
          setProjectConfig({
            language: cachedUser.preferredLanguage,
            country: cachedUser.preferredCountry
          });
        }
        if (cachedUser.uiLanguage) {
          setUiLanguage(cachedUser.uiLanguage);
          // CRITICAL: Initialize i18n with cached user's UI language
          const cachedLangCode = getLanguageCode(cachedUser.uiLanguage);
          initI18n(cachedLangCode).then(() => {
            setLanguage(cachedLangCode).catch(err => {
              console.warn('Failed to set language from cache:', err);
            });
          }).catch(err => {
            console.warn('Failed to init i18n from cache:', err);
          });
        }
        
        // If user is logged in, mark language as set (even if not explicitly set before)
        // This prevents the modal from showing on refresh
        if (!localStorage.getItem('orca_language_set')) {
          localStorage.setItem('orca_language_set', 'true');
        }
      }

      // Then verify with backend (with timeout protection)
      try {
        // Use Promise.race to add timeout protection
        const userPromise = authService.getCurrentUser().catch(err => {
          console.warn('getCurrentUser failed:', err);
          return null;
        });
        const timeoutPromise = new Promise<null>((resolve) => 
          setTimeout(() => {
            // Only log timeout if backend is actually not responding (silent in dev mode)
            if (process.env.NODE_ENV === 'production') {
              console.warn('Backend connection timeout - continuing without user verification');
            }
            resolve(null);
          }, 5000) // Reduced to 5 seconds for faster loading
        );
        
        const user = await Promise.race([userPromise, timeoutPromise]) as any;
        
    if (user) {
      setCurrentUser(user);
      setView('dashboard');
          
          // Load user language preferences
          if (user.preferredLanguage && user.preferredCountry) {
            setProjectConfig({
              language: user.preferredLanguage,
              country: user.preferredCountry
            });
          }
          if (user.uiLanguage) {
            setUiLanguage(user.uiLanguage);
            // CRITICAL: Initialize i18n with user's UI language
            const userLangCode = getLanguageCode(user.uiLanguage);
            initI18n(userLangCode).then(() => {
              setLanguage(userLangCode).catch(err => {
                console.warn('Failed to set language:', err);
              });
            }).catch(err => {
              console.warn('Failed to init i18n:', err);
            });
          }
          
          // If user is logged in, mark language as set (even if not explicitly set before)
          // This prevents the modal from showing on refresh
          if (!localStorage.getItem('orca_language_set')) {
            localStorage.setItem('orca_language_set', 'true');
          }
          
          // Check if email needs verification (priority) - admins are excluded
          if (!user.emailVerified && user.role !== 'admin') {
            setShowVerificationModal(true);
            setPendingVerificationEmail(user.email);
          }
          // Don't show language modal for logged-in users - they can change it from settings
    } else {
      setView('landing');
          // Show language modal on landing page if not set (only for non-logged-in users)
          const hasLanguagePreference = localStorage.getItem('orca_language_set');
          if (!hasLanguagePreference) {
            setTimeout(() => setShowLanguageModalOnLanding(true), 1000);
          }
        }
      } catch (error: any) {
        console.error('Init error:', error);
        
        // Always set view to landing on error to ensure app renders
        setView('landing');
        
        const hasLanguagePreference = localStorage.getItem('orca_language_set');
        
        // Handle timeout or connection errors
        if (error && error.message && (error.message.includes('timeout') || error.message.includes('Cannot connect'))) {
          console.warn('Backend not responding - showing landing page');
          // If backend is not running, show landing page but keep cached user if available
          if (cachedUser) {
            // Keep cached user but show warning
            console.warn('Using cached user data - backend connection failed');
            setCurrentUser(cachedUser);
          }
          if (!hasLanguagePreference) {
            setTimeout(() => setShowLanguageModalOnLanding(true), 1000);
          }
        } else if (error && error.message && error.message.includes('already logged in')) {
          // If it's a session conflict, clear everything and show login
          setCurrentUser(null);
          // Don't show language modal if session conflict - user needs to login
        } else {
          // Any other error - just show landing page
          if (!hasLanguagePreference) {
            setTimeout(() => setShowLanguageModalOnLanding(true), 1000);
          }
        }
      } finally {
        // Always mark as initialized, even if there was an error
        // This ensures the app never gets stuck in loading state
        try {
          console.log('Initialization complete, isInitialized set to true');
          setIsInitialized(true);
          // Hide loading screen immediately - no delay
          setIsAppLoading(false);
          // Ensure view is always set
          if (!view) {
            console.warn('View not set in finally block, setting to landing');
            setView('landing');
          }
        } catch (setStateError) {
          console.error('Error setting initialization state:', setStateError);
          // Force show even if state setting fails
          setForceShow(true);
        }
      }
    };
    initUser();
  }, []);
  
  // Listen for URL changes and handle 404 routing
  useEffect(() => {
    const handleRouteCheck = () => {
      const pathname = window.location.pathname;
      const validRoutes: Record<string, ViewType> = {
        '/': 'landing',
        '/dashboard': 'dashboard',
        '/tools': 'tools',
        '/admin': 'admin',
        '/profile': 'profile',
        '/seo-guide': 'seo-guide',
        '/ai-tools': 'ai-tools',
        '/reporting': 'reporting',
        '/strategy': 'strategy',
        '/rank-tracking': 'rank-tracking',
        '/jsonld': 'jsonld',
        '/gsc-analyzer': 'gsc-analyzer',
        '/speed-optimizer': 'speed-optimizer',
        '/designmind': 'designmind',
        '/seo-resources': 'seo-resources',
        '/backlinks': 'backlinks',
        '/consulting': 'consulting',
        '/privacy-policy': 'privacy-policy',
        '/terms-of-service': 'terms-of-service',
        '/refund-policy': 'refund-policy',
        '/shipping-policy': 'shipping-policy',
        '/contact': 'contact',
        '/services': 'services',
        '/advantages': 'advantages',
        '/how-to-use': 'how-to-use',
        '/faq': 'faq'
      };

      // If pathname doesn't match any valid route, set to 404
      if (pathname !== '/' && !validRoutes[pathname] && !currentUser) {
        setView('404');
      } else if (validRoutes[pathname]) {
        setView(validRoutes[pathname]);
      }
    };

    // Check route on mount and when pathname changes
    handleRouteCheck();
    
    // Listen for popstate (back/forward navigation)
    window.addEventListener('popstate', handleRouteCheck);
    
    return () => {
      window.removeEventListener('popstate', handleRouteCheck);
    };
  }, [currentUser]);
  
  // Listen for language changes and update RTL direction + ensure header visibility
  useEffect(() => {
    const handleLanguageChange = async (event: CustomEvent) => {
      const langCode = event.detail;
      const { getTextDirection } = await import('./services/i18n');
      const direction = getTextDirection(langCode);
      document.documentElement.setAttribute('dir', direction);
      document.documentElement.setAttribute('lang', langCode);
      
      // Ensure header stays visible after language change
      setTimeout(() => {
        const header = document.querySelector('header.sticky');
        if (header) {
          const headerEl = header as HTMLElement;
          headerEl.style.position = 'sticky';
          headerEl.style.top = '0';
          headerEl.style.left = '0';
          headerEl.style.right = '0';
          headerEl.style.zIndex = '30';
          headerEl.style.width = '100%';
          headerEl.style.display = 'flex';
        }
      }, 100);
    };

    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    return () => {
      window.removeEventListener('languageChange', handleLanguageChange as EventListener);
    };
  }, []);
  
  // Listen for showPaywall event from child components (like BacklinkBuilder)
  useEffect(() => {
    const handleShowPaywall = () => {
      setShowPaywall(true);
    };
    window.addEventListener('showPaywall', handleShowPaywall);
    return () => window.removeEventListener('showPaywall', handleShowPaywall);
  }, []);

  // Separate effect to show language modal on landing page - MOST IMPORTANT FIX
  useEffect(() => {
    // Don't show modal if user is logged in (even if cached)
    const cachedUser = authService.getCachedUser();
    if (cachedUser || currentUser) {
      setShowLanguageModalOnLanding(false);
      return undefined;
    }

    // Check for language preference
    const hasLanguagePreference = localStorage.getItem('orca_language_set');
    
    // Only show modal if:
    // 1. No language preference is set
    // 2. User is not logged in (not cached, not current)
    // 3. Not in auth view (login/register)
    // 4. On landing page
    let timer: NodeJS.Timeout | null = null;
    if (!hasLanguagePreference && !cachedUser && !currentUser && !authView && view === 'landing') {
      timer = setTimeout(() => {
        setShowLanguageModalOnLanding(true);
      }, 500);
    } else {
      // Close modal if conditions not met
      setShowLanguageModalOnLanding(false);
    }
    
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [view, currentUser, authView]);

  // AUTH HANDLERS
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setIsLoading(true);
    
    try {
      console.log('[Login] Attempting login for:', email);
      const user = await authService.login(email, password);
      console.log('[Login] Login successful, user:', user);
      
      setCurrentUser(user);
      setAuthView(null);
      setView('dashboard');
      setAuthError('');
      
      // Clear form
      setEmail('');
      setPassword('');
      
      // Cookies are set automatically by server - no need to check localStorage
      // Note: getCurrentUser() might fail immediately after login due to cookie propagation delay
      // This is normal - cookies will be available on subsequent requests
      console.log('[Login] Login successful, cookies should be set by server');
    } catch (err: any) {
      console.error('[Login] Login error:', err);
      const errorMessage = err.message || err.error || t('auth.loginError') || 'Login failed. Please check your credentials.';
      console.error('[Login] Error message:', errorMessage);
      
      // Track error
      errorTracker.trackError(err, {
        source: 'frontend',
        component: 'App',
        action: 'handleLogin',
        context: {
          email,
          errorMessage,
          endpoint: '/auth/login'
        }
      });
      
      setAuthError(errorMessage);
      // Keep user on login page
      setAuthView('login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setIsLoading(true);
    
    // Validate WhatsApp number
    if (!whatsappNumber || whatsappNumber.trim() === '') {
      setAuthError(t('auth.whatsappRequired') || 'WhatsApp number is required');
      setIsLoading(false);
      return;
    }
    
    try {
      console.log('[Register] Starting registration...');
      const response = await authService.register(name, email, password, whatsappNumber);
      const user = response;
      console.log('[Register] Registration successful, user:', user);
      
      // Check if email needs verification
      if (!user.emailVerified) {
        setCurrentUser(user);
        setPendingVerificationEmail(email);
        setShowVerificationModal(true);
        setAuthView(null);
        // Clear form
        setName('');
        setEmail('');
        setPassword('');
        setWhatsappNumber('');
        console.log('[Register] Email verification required, modal shown');
      } else {
        // Already verified (shouldn't happen on new registration, but handle it)
      setCurrentUser(user);
      setAuthView(null);
      setView('dashboard');
      }
      setAuthError('');
    } catch (err: any) {
      console.error('[Register] Registration error:', err);
      setAuthError(err.message || t('auth.registerError') || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailVerified = (verifiedUser: User) => {
    setCurrentUser(verifiedUser);
    setShowVerificationModal(false);
    setPendingVerificationEmail('');
    setView('dashboard');
    // Show success message
    alert('✅ Email verified! You now have 10 free credits.');
  };

  const handleLogout = async () => {
    await authService.logout(); // Now async - clears cookies via API
    setCurrentUser(null);
    setView('landing');
    setReport(null);
    localStorage.removeItem('orca_language_set');
  };

  const handleLanguageSave = async (analysisLanguage: string, uiLanguage: string, aiAssistantLanguage: string, country: string) => {
    try {
      // Update local state
      setProjectConfig({ language: analysisLanguage, country });
      setUiLanguage(uiLanguage);
      localStorage.setItem('orca_ui_language', uiLanguage);
      
      // Initialize i18n with new UI language
      const langCode = getLanguageCode(uiLanguage);
      
      // CRITICAL: Load translations FIRST, then set language
      await initI18n(langCode);
      await setLanguage(langCode);
      
      // Update RTL/LTR direction based on language
      const { getTextDirection } = await import('./services/i18n');
      const direction = getTextDirection(langCode);
      document.documentElement.setAttribute('dir', direction);
      document.documentElement.setAttribute('lang', langCode);
      
      // Save to backend if user is logged in
      if (currentUser) {
        await authService.updatePreferences(analysisLanguage, uiLanguage, aiAssistantLanguage, country);
        // Update current user
        const updatedUser = { 
          ...currentUser, 
          preferredLanguage: analysisLanguage,
          uiLanguage: uiLanguage,
          aiAssistantLanguage: aiAssistantLanguage,
          preferredCountry: country 
        };
      setCurrentUser(updatedUser);
        localStorage.setItem('orca_user', JSON.stringify(updatedUser));
      } else {
        // Save to localStorage for non-logged-in users
        localStorage.setItem('orca_analysis_language', analysisLanguage);
        localStorage.setItem('orca_ui_language', uiLanguage);
        localStorage.setItem('orca_ai_assistant_language', aiAssistantLanguage);
        localStorage.setItem('orca_country', country);
      }
      
      // Mark as set
      localStorage.setItem('orca_language_set', 'true');
      setShowLanguageModal(false);
      setShowLanguageModalOnLanding(false);
      
      // CRITICAL: Dispatch language change event AFTER translations are loaded
      // Use setTimeout to ensure all state updates are processed
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('languageChange', { detail: langCode }));
        // Also trigger storage event for cross-component updates
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'orca_ui_language_code',
          newValue: langCode,
          storageArea: localStorage
        }));
      }, 100);
    } catch (error: any) {
      console.error('Failed to save preferences:', error);
      // Still update local state even if backend fails
      setProjectConfig({ language: analysisLanguage, country });
      setUiLanguage(uiLanguage);
      localStorage.setItem('orca_ui_language', uiLanguage);
      // Try to set language anyway
      const langCode = getLanguageCode(uiLanguage);
      await initI18n(langCode);
      await setLanguage(langCode);
      
      // Update RTL/LTR direction based on language
      const { getTextDirection } = await import('./services/i18n');
      const direction = getTextDirection(langCode);
      document.documentElement.setAttribute('dir', direction);
      document.documentElement.setAttribute('lang', langCode);
      
      // CRITICAL: Force a re-render of all components by dispatching language change event
      window.dispatchEvent(new CustomEvent('languageChange', { detail: langCode }));
      
      // Also force a React state update to trigger re-renders
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'orca_ui_language_code',
        newValue: langCode,
        storageArea: localStorage
      }));
      
      localStorage.setItem('orca_language_set', 'true');
      setShowLanguageModal(false);
      setShowLanguageModalOnLanding(false);
      
      // Trigger language change event for real-time updates (no page reload needed)
      // The event will be handled by components that listen to it
    }
  };

  // Credit check queue to prevent concurrent requests (module-level to persist across renders)
  // Shared credit check queue for debouncing (but per-tool-type tracking)
  const creditCheckQueue = {
    lastCheck: 0,
    debounce: 500 // 500ms debounce
  };
  
  // Track concurrent credit checks per tool type to allow parallel execution
  const creditCheckPromises = new Map<string, Promise<boolean>>();
  
  const checkCredits = async (toolType: string = 'default'): Promise<boolean> => {
    if (!currentUser) {
      console.error('No current user found');
      alert('Please login first');
      return false;
    }
    
    // Allow concurrent credit checks for different tools
    // Each tool type can have its own credit check running in parallel
    const now = Date.now();
    const existingPromise = creditCheckPromises.get(toolType);
    
    // If there's an existing promise for this tool type and it's recent, reuse it
    if (existingPromise && (now - creditCheckQueue.lastCheck) < creditCheckQueue.debounce) {
      console.log(`Credit check for ${toolType} debounced, reusing existing request`);
      return existingPromise;
    }
    
    // Create new credit check promise for this tool type
    creditCheckQueue.lastCheck = now;
    const creditPromise = (async () => {
      try {
        // No token check needed - cookies are sent automatically
        // If session is invalid, API will return 401 and we'll handle it
        
        console.log(`[${toolType}] Checking credits for user:`, currentUser.email);
        const result = await authService.deductCredit();
        console.log(`[${toolType}] Credit check result:`, result);
        // Update user credits
        const updatedUser = { ...currentUser, credits: result.credits };
        setCurrentUser(updatedUser);
        localStorage.setItem('orca_user', JSON.stringify(updatedUser));
      return true;
    } catch (err: any) {
        console.error(`[${toolType}] Credit check error:`, err);
        if (err.message.includes('Insufficient credits') || err.message.includes('402')) {
        setShowPaywall(true);
        return false;
      }
        // Don't show alert for timeout errors when multiple tools run - just log
        if (err.message.includes('timeout') || err.message.includes('Request timeout')) {
          console.warn(`[${toolType}] Credit check timeout (may be due to concurrent requests)`);
          // Return false but don't block - let the tool try anyway
      return false;
    }
        // Show error to user for other errors
        alert(`Error checking credits: ${err.message || 'Unknown error'}. Please check console.`);
        return false;
      } finally {
        // Clear promise for this tool type after a delay to allow reuse
        setTimeout(() => {
          creditCheckPromises.delete(toolType);
        }, creditCheckQueue.debounce);
      }
    })();
    
    // Store promise for this tool type and return it
    creditCheckPromises.set(toolType, creditPromise);
    return creditPromise;
  };

  const handleUpgrade = async (plan: PlanType) => {
    if (!currentUser) {
      alert('Please login first');
      setShowPaywall(false);
      setAuthView('login');
      return;
    }
    
    // No token check needed - cookies are sent automatically
    // If session is invalid, API will return 401 and we'll handle it
    
    // Verify user session is still valid before proceeding
    try {
      const validUser = await authService.getCurrentUser();
      if (!validUser) {
        alert('Session expired. Please login again.');
        setShowPaywall(false);
        handleLogout();
        setAuthView('login');
        return;
      }
      // Update current user in case it changed
      setCurrentUser(validUser);
    } catch (error: any) {
      console.error('Session verification failed:', error);
      alert('Session expired. Please login again.');
      setShowPaywall(false);
      handleLogout();
      setAuthView('login');
      return;
    }
    
    setIsLoading(true);
    try {
      const { approvalUrl, orderId } = await authService.createPayPalOrder(plan);
      // Store orderId and plan for after payment
      localStorage.setItem('pending_payment', JSON.stringify({ orderId, plan }));
      // Redirect to PayPal
      if (approvalUrl) {
        window.location.href = approvalUrl;
      } else {
        throw new Error('No PayPal approval URL received');
      }
    } catch (error: any) {
      console.error('Payment setup error:', error);
      // If it's an auth error, logout and show login
      if (error.message?.includes('token') || error.message?.includes('authorization') || error.message?.includes('401')) {
        alert('Session expired. Please login again.');
      setShowPaywall(false);
        handleLogout();
        setAuthView('login');
      } else {
        alert(`Payment setup failed: ${error.message}`);
      }
      setIsLoading(false);
    }
  };

  // TOOL WRAPPERS (To inject Credit Check)
  const fetchHtmlContent = async (url: string): Promise<string> => {
    let targetUrl = url.trim();
    if (!/^https?:\/\//i.test(targetUrl)) targetUrl = 'https://' + targetUrl;
    const strategies = [
      { name: 'CorsProxy', fetch: async (u: string) => (await fetch(`https://corsproxy.io/?${encodeURIComponent(u)}`)).text() },
      { name: 'AllOrigins', fetch: async (u: string) => (await (await fetch(`https://api.allorigins.win/get?url=${encodeURIComponent(u)}`)).json()).contents }
    ];
    for (const s of strategies) {
      try { const c = await s.fetch(targetUrl); if (c) return c; } catch {}
    }
    throw new Error("Could not fetch source code. Try pasting it manually.");
  };

  const runAudit = async (url: string, html: string) => {
    console.log('runAudit called with:', { url, hasHtml: !!html, hasUser: !!currentUser });
    if (!currentUser) {
      console.error('No user found in runAudit');
      alert('Please login first');
      return;
    }
    
    if (!url || !url.trim()) {
      alert('Please enter a URL');
      return;
    }
    
    console.log('Checking credits before audit...');
    const hasCredits = await checkCredits('audit');
    console.log('Credit check result:', hasCredits);
    
    if (hasCredits) {
      const taskId = `audit-${Date.now()}`;
      const task: BackgroundTask = {
        id: taskId,
        type: 'audit',
        status: 'running',
        message: `Auditing ${url}...`,
        startTime: Date.now()
      };
      
      setBackgroundTasks(prev => new Map(prev).set(taskId, task));
      setAuditLoading(true);
      setAuditLoadingMessage('Starting audit...');
      setPartialReport(null);
      
      // Initialize progress steps - first step running, others pending
      const initialProgress = new Map<string, AuditProgress>();
      initialProgress.set('extraction', { 
        step: 'extraction', 
        title: t('auditProgress.fetchingData') || 'Fetching & Extracting Data', 
        status: 'running', 
        message: t('auditProgress.startingExtraction') || 'Starting extraction...' 
      });
      initialProgress.set('enhanced', { 
        step: 'enhanced', 
        title: t('auditProgress.enhancedAnalysis') || 'Enhanced Analysis', 
        status: 'running', 
        message: t('auditProgress.waitingExtraction') || 'Waiting for extraction to complete...' 
      });
      initialProgress.set('ai-analysis', { 
        step: 'ai-analysis', 
        title: t('auditProgress.aiAnalysis') || 'AI Analysis', 
        status: 'running', 
        message: t('auditProgress.waitingEnhanced') || 'Waiting for enhanced analysis...' 
      });
      initialProgress.set('link-performance', { 
        step: 'link-performance', 
        title: t('auditProgress.linkPerformance') || 'Link & Performance Analysis', 
        status: 'running', 
        message: t('auditProgress.waitingAI') || 'Waiting for AI analysis...' 
      });
      setAuditProgress(initialProgress);
      
      // Run audit with progressive updates
      (async () => {
      try {
        // ⚡ STEP 1: INSTANT CLIENT-SIDE EXTRACTION (100-200ms)
        console.time('Client-side extraction');
        let instantMetrics: InstantMetrics | null = null;
        
        try {
          instantMetrics = await ClientSideExtractor.extractInstant(url);
          console.timeEnd('Client-side extraction');
          console.log('✅ Client-side extraction completed:', instantMetrics);
          
          // ✅ Display instant results immediately
          setPartialReport({
            url,
            overallScore: 0, // Will be calculated by AI
            summary: t('auditProgress.analyzingWebsite') || 'Analyzing website...',
            categories: {
              'On-Page SEO': { score: 0, totalIssues: 0, criticalIssues: 0 },
              'Technical SEO': { score: 0, totalIssues: 0, criticalIssues: 0 },
              'Off-Page SEO': { score: 0, totalIssues: 0, criticalIssues: 0 },
              'AEO (Answer Engine Opt.)': { score: 0, totalIssues: 0, criticalIssues: 0 }
            },
            issues: [],
            keywordsDetected: [],
            instantMetrics: instantMetrics as any, // Show this immediately!
            timestamp: new Date().toISOString() // Add as any property
          } as any);
          
          // Update extraction progress
          setAuditProgress(prev => {
            const updated = new Map(prev);
            updated.set('extraction', {
              step: 'extraction',
              title: t('auditProgress.fetchingData') || 'Fetching & Extracting Data',
              status: 'completed',
              message: `${t('auditProgress.extractedData') || 'Extracted data'} ${instantMetrics.title ? instantMetrics.title.substring(0, 50) : ''} (${instantMetrics.responseTime})`,
              progress: 100,
              duration: parseInt(instantMetrics.responseTime) || 0
            });
            return updated;
          });
          
        } catch (clientError: any) {
          // CORS errors are expected for external sites - don't treat as error
          if (clientError.message?.includes('CORS_BLOCKED') || 
              clientError.message?.includes('Failed to fetch') ||
              clientError.name === 'TypeError') {
            console.log('Client-side extraction blocked (CORS/network), using server-side extraction');
          } else {
            console.warn('Client-side extraction failed, will use server-side:', clientError);
          }
          // Continue with server-side extraction - set instantMetrics to null
          instantMetrics = null;
        }
        
        // 🔍 STEP 2: Determine if we need Puppeteer
        const needsHeadless = instantMetrics ? ClientSideExtractor.needsPuppeteer(instantMetrics) : true;
        
        console.log('Calling analyzeContentProgressive API...', { needsHeadless, hasInstantMetrics: !!instantMetrics });
        const res = await analyzeContentProgressive(
          url, 
          html || '', 
          {
            onProgress: (progress: AuditProgress) => {
              console.log('Progress update:', progress);
              setAuditProgress(prev => {
                const updated = new Map(prev);
                updated.set(progress.step, progress);
                return updated;
              });
              
              setAuditLoadingMessage(progress.message);
              
              // If we have partial report data, show it
              if (progress.data && progress.step === 'ai-analysis') {
                setPartialReport(progress.data);
              }
            },
            onComplete: (report: SeoReport) => {
              console.log('Analysis completed:', report);
              setReport(report);
              setPartialReport(null);
              setAuditLoading(false);
              setAuditLoadingMessage('');
              
              setBackgroundTasks(prev => {
                const updated = new Map(prev);
                updated.set(taskId, { ...task, status: 'completed', result: report });
                return updated;
              });
              
              setTaskNotifications(prev => [...prev, {
                id: `notif-${Date.now()}`,
                message: `Audit completed for ${url}`,
                type: 'success'
              }]);
            },
            onError: (error: string) => {
              console.error('Audit error:', error);
              setBackgroundTasks(prev => {
                const updated = new Map(prev);
                updated.set(taskId, { ...task, status: 'failed', error });
                return updated;
              });
              
              setTaskNotifications(prev => [...prev, {
                id: `notif-${Date.now()}`,
                message: `Audit failed: ${error}`,
                type: 'error'
              }]);
              
              if (view === 'tools' && activeModule === 'audit') {
                alert(`Analysis failed: ${error}. Please check console for details.`);
              }
              setAuditLoadingMessage('');
              setAuditLoading(false);
            }
          },
          {
            usePuppeteer: needsHeadless, // Only if needed
            instantMetrics: instantMetrics || undefined // Pass what we already have
          }
        );
          
          // Final report is already set in onComplete, but ensure it's set here too
          if (res) {
        setReport(res);
            setPartialReport(null);
            setAuditLoading(false);
            setAuditLoadingMessage('');
          }
        } catch (e: any) {
          console.error('Audit error:', e);
          setBackgroundTasks(prev => {
            const updated = new Map(prev);
            updated.set(taskId, { ...task, status: 'failed', error: e.message });
            return updated;
          });
          
          setTaskNotifications(prev => [...prev, {
            id: `notif-${Date.now()}`,
            message: `Audit failed: ${e.message || 'Unknown error'}`,
            type: 'error'
          }]);
          
          if (view === 'tools' && activeModule === 'audit') {
            alert(`Analysis failed: ${e.message || 'Unknown error'}. Please check console for details.`);
          }
          setAuditLoadingMessage('');
          setAuditLoading(false);
        }
      })();
    } else {
      console.log('Credit check failed, not running audit');
    }
  };

  const runKeywords = async (seed: string, intent: string) => {
    if (!currentUser) {
      alert('Please login first');
      return undefined;
    }
    
    if (await checkCredits('keywords')) {
      setKeywordsLoading(true);
      try {
        const res = await generateKeywords(seed, projectConfig.country, projectConfig.language, intent);
        return res;
      } catch (e: any) {
        console.error('Keyword error:', e);
        alert(`Keyword search failed: ${e.message || 'Unknown error'}`);
        return undefined;
      } finally {
        setKeywordsLoading(false);
      }
    }
    return undefined;
  };

  const runCompetitor = async (myUrl: string, compUrl: string) => {
    if (!currentUser) {
      alert('Please login first');
      return undefined;
    }
    
    if (await checkCredits('competitor')) {
      const taskId = `competitor-${Date.now()}`;
      const task: BackgroundTask = {
        id: taskId,
        type: 'competitor',
        status: 'running',
        message: `Analyzing competitor: ${compUrl}...`,
        startTime: Date.now()
      };
      
      setBackgroundTasks(prev => new Map(prev).set(taskId, task));
      setCompetitorLoading(true);
      
      // Run in background
      return (async () => {
        try {
          let myContent = '';
          let compContent = '';
          
          try {
            myContent = await fetchHtmlContent(myUrl);
          } catch (e) {
            console.warn('Failed to fetch myUrl content:', e);
          }
          
          try {
            compContent = await fetchHtmlContent(compUrl);
          } catch (e) {
            console.warn('Failed to fetch compUrl content:', e);
          }
          
        const res = await analyzeCompetitor(myUrl, myContent, compUrl, compContent);
          
          setBackgroundTasks(prev => {
            const updated = new Map(prev);
            updated.set(taskId, { ...task, status: 'completed', result: res });
            return updated;
          });
          
          setTaskNotifications(prev => [...prev, {
            id: `notif-${Date.now()}`,
            message: `Competitor analysis completed`,
            type: 'success'
          }]);
          
          setCompetitorLoading(false);
        return res;
        } catch (e: any) {
          console.error('Competitor error:', e);
          setBackgroundTasks(prev => {
            const updated = new Map(prev);
            updated.set(taskId, { ...task, status: 'failed', error: e.message });
            return updated;
          });
          
          setTaskNotifications(prev => [...prev, {
            id: `notif-${Date.now()}`,
            message: `Competitor analysis failed: ${e.message || 'Unknown error'}`,
            type: 'error'
          }]);
          
          setCompetitorLoading(false);
          if (view === 'tools' && activeModule === 'competitors') {
            alert(`Competitor analysis failed: ${e.message || 'Unknown error'}`);
          }
          return undefined;
        }
      })();
    }
    return undefined;
  };

  // --- RENDER VIEWS ---
  
  // Show minimal loading state only on very first render (with timeout fallback)
  useEffect(() => {
    if (!isInitialized || isAppLoading) {
      // Aggressive fallback: force initialization after 2 seconds to prevent infinite loading
      const timeout = setTimeout(() => {
        console.warn('Initialization timeout - forcing app to load');
        setIsInitialized(true);
        setIsAppLoading(false);
        // Ensure we have a view set
        if (!view) {
          setView('landing');
        }
      }, 2000);
      
      return () => clearTimeout(timeout);
    }
  }, [isInitialized, isAppLoading, view]);

  // CRITICAL: Always ensure we have a valid view state
  useEffect(() => {
    // If somehow view is not set after initialization, set it to landing
    if (isInitialized && !view) {
      console.warn('View not set after initialization, setting to landing');
      setView('landing');
    }
  }, [isInitialized, view]);

  // Force show after timeout to prevent infinite loading (reduced to 1 second)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isInitialized || isAppLoading) {
        console.warn('Force showing app after 1 second timeout');
        setForceShow(true);
        setIsInitialized(true);
        setIsAppLoading(false);
        if (!view) {
          setView('landing');
        }
      }
    }, 1000); // Reduced from 2000ms to 1000ms for faster loading
    return () => clearTimeout(timer);
  }, [isInitialized, isAppLoading, view]);
  
  // Additional safety: force initialization after 3 seconds maximum
  useEffect(() => {
    const maxTimeout = setTimeout(() => {
      console.error('Maximum timeout reached - forcing app to show');
      setForceShow(true);
      setIsInitialized(true);
      setIsAppLoading(false);
      if (!view) {
        setView('landing');
      }
    }, 3000);
    return () => clearTimeout(maxTimeout);
  }, []);

  // Only show loading if truly not initialized (with fallback timeout)
  // Add immediate fallback to prevent blank page
  if ((!isInitialized || isAppLoading) && !forceShow) {
    // Show loading screen but with a shorter timeout
    return <LoadingScreen />;
  }
  
  // Safety check: if view is not set, force it to landing
  if (!view) {
    console.warn('View is not set, forcing to landing');
    setView('landing');
    // Return a simple div while view is being set to prevent blank page
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-400 mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }
  
  // Debug log to see what view is being rendered
  console.log('[App] Rendering view:', view, 'isInitialized:', isInitialized, 'isAppLoading:', isAppLoading, 'forceShow:', forceShow);
  
  // 1. LANDING & AUTH
  if (view === 'landing' && !currentUser) {
    if (authView === 'login') return (
       <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
         <div className="bg-slate-800 p-8 rounded-2xl w-full max-w-md border border-slate-700 animate-fadeIn">
            <h2 className="text-2xl font-bold text-white mb-6">Login to OrcaTools</h2>
            {authError && <div className="bg-red-500/10 text-red-400 p-3 rounded mb-4 text-sm">{authError}</div>}
            <form onSubmit={handleLogin} className="space-y-4" method="post" action="#" autoComplete="on">
              {authError && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded text-sm animate-fadeIn">
                  {authError}
                </div>
              )}
              <input 
                type="email" 
                name="email"
                id="login-email"
                placeholder="Email" 
                value={email} 
                onChange={e => setEmail(e.target.value)} 
                className="w-full bg-slate-900 border border-slate-600 p-3 rounded text-white" 
                autoComplete="username"
                required 
                disabled={isLoading}
              />
              <input 
                type="password" 
                name="password"
                id="login-password"
                placeholder="Password" 
                value={password} 
                onChange={e => setPassword(e.target.value)} 
                className="w-full bg-slate-900 border border-slate-600 p-3 rounded text-white" 
                autoComplete="current-password"
                required 
                disabled={isLoading}
              />
              <button 
                type="submit"
                disabled={isLoading}
                className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-400 disabled:cursor-not-allowed text-white p-3 rounded font-bold transition-colors"
              >
                {isLoading ? 'Logging in...' : 'Access Dashboard'}
              </button>
            </form>
            <button onClick={() => setAuthView('register')} className="mt-4 text-slate-400 text-sm hover:text-white block w-full text-center">New here? Create account</button>
            <button onClick={() => setAuthView(null)} className="mt-2 text-slate-500 text-xs block mx-auto">Back to Home</button>
         </div>
       </div>
    );
    if (authView === 'register') return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
         <div className="bg-slate-800 p-8 rounded-2xl w-full max-w-md border border-slate-700 animate-fadeIn">
            <h2 className="text-2xl font-bold text-white mb-6">Join the Movement</h2>
            {authError && <div className="bg-red-500/10 text-red-400 p-3 rounded mb-4 text-sm">{authError}</div>}
            <form onSubmit={handleRegister} className="space-y-4" method="post" action="#" autoComplete="on">
              <input 
                type="text" 
                name="name"
                id="register-name"
                placeholder="Your Name" 
                value={name} 
                onChange={e => setName(e.target.value)} 
                className="w-full bg-slate-900 border border-slate-600 p-3 rounded text-white" 
                autoComplete="name"
                required 
                disabled={isLoading} 
              />
              <input 
                type="email" 
                name="email"
                id="register-email"
                placeholder="Email" 
                value={email} 
                onChange={e => setEmail(e.target.value)} 
                className="w-full bg-slate-900 border border-slate-600 p-3 rounded text-white" 
                autoComplete="email"
                required 
                disabled={isLoading} 
              />
              <input 
                type="password" 
                name="password"
                id="register-password"
                placeholder="Password" 
                value={password} 
                onChange={e => setPassword(e.target.value)} 
                className="w-full bg-slate-900 border border-slate-600 p-3 rounded text-white" 
                autoComplete="new-password"
                required 
                disabled={isLoading} 
              />
              <input 
                type="tel" 
                name="whatsapp"
                id="register-whatsapp"
                placeholder="WhatsApp Number *" 
                value={whatsappNumber} 
                onChange={e => setWhatsappNumber(e.target.value)} 
                className="w-full bg-slate-900 border border-slate-600 p-3 rounded text-white" 
                autoComplete="tel"
                required 
                disabled={isLoading} 
              />
              <div className="bg-indigo-900/30 border border-indigo-500/30 rounded-lg p-3 text-xs text-slate-300">
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  <div>
                    <strong className="text-white">All communication via WhatsApp</strong>
                    <p className="mt-1 text-slate-400">After signing up, all support, payment processing, and account management will be handled via WhatsApp. Make sure to provide your active WhatsApp number.</p>
                  </div>
                </div>
              </div>
              <button type="submit" disabled={isLoading} className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-800 disabled:cursor-not-allowed text-white p-3 rounded font-bold flex items-center justify-center gap-2">
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Creating Account...
                  </>
                ) : (
                  'Start Free Trial (10 Credits)'
                )}
              </button>
            </form>
            <button onClick={() => setAuthView('login')} className="mt-4 text-slate-400 text-sm hover:text-white block w-full text-center">Already have an account? Login</button>
            <button onClick={() => setAuthView(null)} className="mt-2 text-slate-500 text-xs block mx-auto">Back to Home</button>
         </div>
       </div>
    );
    return <LandingPage 
      onGetStarted={() => setAuthView('register')} 
      onLogin={() => setAuthView('login')} 
      onNavigateToLegal={(page) => setView(page)}
    />;
  }

  // 2. PROTECT DASHBOARD - Only logged in users can access
  if (view === 'dashboard' && !currentUser) {
    setView('landing');
  }

  // 3. DASHBOARD LAYOUT
  // Check if user is disapproved (not admin)
  const isDisapproved = currentUser && currentUser.role !== 'admin' && currentUser.isApproved === false;
  
  // Check if we're on a legal or info page (no sidebar needed, but show header/footer)
  const isLegalPage = ['privacy-policy', 'terms-of-service', 'refund-policy', 'shipping-policy', 'contact', 'services', 'advantages', 'how-to-use', 'faq', '404'].includes(view);
  
  // Check if we're on landing page (has its own header/footer)
  const isLandingPage = view === 'landing' && !currentUser;
  
  // Sidebar should only show for logged-in users on non-legal pages
  const shouldShowSidebar = currentUser && !isLegalPage;

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-200 font-sans flex overflow-hidden relative">
      {/* Task Notifications */}
      <div className="fixed top-4 right-4 z-[200] space-y-2">
        {taskNotifications.map(notif => (
          <div
            key={notif.id}
            className={`px-4 py-3 rounded-lg shadow-lg backdrop-blur-sm border ${
              notif.type === 'success' 
                ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300' 
                : 'bg-red-500/20 border-red-500/50 text-red-300'
            } animate-fadeIn max-w-sm`}
          >
            <div className="flex items-center gap-2">
              {notif.type === 'success' ? (
                <CheckCircle2 className="w-4 h-4" />
              ) : (
                <AlertCircle className="w-4 h-4" />
              )}
              <span className="text-sm font-medium">{notif.message}</span>
            </div>
          </div>
        ))}
      </div>
      
      {/* Under Review Overlay for Disapproved Users */}
      {isDisapproved && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-slate-800 border-2 border-yellow-500/50 rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl">
            <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-yellow-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Account Under Review</h2>
            <p className="text-slate-300 mb-2">
              Your account is currently being reviewed by our admin team.
            </p>
            <p className="text-slate-400 text-sm mb-6">
              You'll be able to access all features once your account is approved.
            </p>
            <button
              onClick={handleLogout}
              className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-bold transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      )}

      {/* Blur overlay for disapproved users */}
      {isDisapproved && (
        <div className="absolute inset-0 z-50 bg-black/60 backdrop-blur-md pointer-events-none"></div>
      )}
      
      {/* HEADER - Show on all pages except landing (which has its own) */}
      {!isLandingPage && (
        <CommonHeader 
          onLogin={() => setAuthView('login')}
          onGetStarted={() => setAuthView('register')}
          onNavigateHome={() => {
            if (currentUser) {
              setView('dashboard');
            } else {
              setView('landing');
            }
          }}
          currentUser={currentUser}
          onMenuClick={() => setSidebarOpen(!sidebarOpen)}
          showMenuButton={shouldShowSidebar}
        />
      )}

      {/* SIDEBAR - Only show for logged-in users on non-legal pages */}
      {shouldShowSidebar && (
      <aside className={`fixed inset-y-0 left-0 z-50 w-72 bg-[#1e293b]/90 backdrop-blur-xl border-r border-white/5 transform transition-transform duration-300 md:relative md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} flex flex-col h-screen overflow-hidden mt-16 md:mt-0`}>
        {/* Sidebar Header - Fixed */}
        <div className="p-6 flex-shrink-0 border-b border-white/5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => { setView('dashboard'); setSidebarOpen(false); }}>
            <div className="bg-indigo-600 p-1.5 rounded-lg">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <span className="font-extrabold text-xl tracking-tight text-white">Orca<span className="text-indigo-400">Tools</span></span>
            </div>
            {/* Close button for mobile */}
            <button 
              onClick={() => setSidebarOpen(false)} 
              className="md:hidden text-slate-400 hover:text-white p-1"
            >
              <X size={20} />
            </button>
          </div>

          <div className="bg-slate-800/50 rounded-xl p-4 border border-white/5">
             <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-indigo-500 to-pink-500 flex items-center justify-center font-bold text-xs text-white flex-shrink-0">
                  {currentUser?.name.charAt(0)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-white truncate">{currentUser?.name}</div>
                  <div className="text-[10px] text-slate-400 uppercase">{currentUser?.plan} {t('common.dashboard')}</div>
                </div>
             </div>
             <div className="bg-slate-900 rounded-lg p-2 text-center border border-slate-700">
                <div className="text-xs text-slate-500 uppercase font-bold">{t('common.credits')}</div>
                <div className={`text-xl font-bold ${currentUser?.credits === 0 ? 'text-red-500' : 'text-emerald-400'}`}>
                  {currentUser?.credits}
                </div>
             </div>
             {currentUser?.credits === 0 && (
               <button onClick={() => { setShowPaywall(true); setSidebarOpen(false); }} className="w-full mt-2 bg-indigo-600 text-xs py-1.5 rounded text-white font-bold hover:bg-indigo-500">
                 {t('paywall.title')}
               </button>
             )}
          </div>
          </div>

        {/* Scrollable Navigation */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-4 md:px-6 py-4 space-y-1 scrollbar-thin">
            <button onClick={() => { setView('dashboard'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${view === 'dashboard' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
              <LayoutGrid size={20} /> {t('common.dashboard')}
            </button>
            <div className="px-4 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 mt-4">{t('tools.siteAudit') || 'EXPERT SITE AUDIT'}</div>
            <button onClick={() => { setView('tools'); setActiveModule('audit'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${activeModule === 'audit' && view === 'tools' ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
              <ShieldCheck size={20} /> {t('tools.siteAudit')}
            </button>
            <button onClick={() => { setView('tools'); setActiveModule('keywords'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${activeModule === 'keywords' && view === 'tools' ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
              <Search size={20} /> {t('tools.keywordResearch')}
            </button>
            <button onClick={() => { setView('tools'); setActiveModule('competitors'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${activeModule === 'competitors' && view === 'tools' ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
              <Swords size={20} /> {t('tools.competitorAnalysis')}
            </button>
            <button onClick={() => { setView('rank-tracking'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${view === 'rank-tracking' ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
              <BarChart2 size={20} /> {(() => {
                const text = t('dashboard.rankTracking');
                return text && text !== 'dashboard.rankTracking' ? text : 'SERP Rank Tracking';
              })()}
            </button>
            <button onClick={() => { setView('jsonld'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${view === 'jsonld' ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
              <FileCode size={20} /> {(() => {
                const text = t('dashboard.jsonldAnalyzer');
                return text && text !== 'dashboard.jsonldAnalyzer' ? text : 'JSON-LD Analyzer';
              })()}
            </button>
            <div className="px-4 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 mt-4">{(() => {
              const section = t('tools.resourcesSection');
              return section && section !== 'tools.resourcesSection' ? section : 'RESOURCES & TOOLS';
            })()}</div>
            <button onClick={() => { setView('tools'); setActiveModule('roi'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${activeModule === 'roi' && view === 'tools' ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
              <Calculator size={20} /> {t('tools.roiCalculator')}
            </button>
            <button onClick={() => { setView('seo-resources'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${view === 'seo-resources' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
              <Package size={20} /> {t('seoResources.title') || 'SEO Resources & Plugins'}
            </button>
            <button onClick={() => { setView('backlinks'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${view === 'backlinks' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
              <Link2 size={20} /> {t('backlinks.title') || 'Backlink Builder'}
            </button>
            <button onClick={() => { setView('gsc-analyzer'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${view === 'gsc-analyzer' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
              <BarChart3 size={20} /> {(() => {
                const text = t('gsc.title');
                return text && text !== 'gsc.title' ? text : 'GSC Analyzer';
              })()}
            </button>
            <button onClick={() => { setView('speed-optimizer'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${view === 'speed-optimizer' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
              <Zap size={20} /> {(() => {
                const text = t('speedOptimizer.title');
                return text && text !== 'speedOptimizer.title' ? text : 'Speed Optimizer';
              })()}
            </button>
            <div className="px-4 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 mt-4">{(() => {
              const section = t('tools.aiToolsSection');
              return section && section !== 'tools.aiToolsSection' ? section : 'AI TOOLS';
            })()}</div>
            <button onClick={() => { setView('ai-tools'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${view === 'ai-tools' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
              <Zap size={20} /> {t('aiTools.title') || 'AI-Powered SEO Tools'}
            </button>
            <div className="px-4 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 mt-4">{(() => {
              const section = t('tools.learnSection');
              return section && section !== 'tools.learnSection' ? section : 'LEARN';
            })()}</div>
            <button
              onClick={() => { setView('strategy'); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all text-slate-400 hover:text-white hover:bg-white/5 ${
                view === 'strategy' ? 'bg-indigo-600 text-white' : ''
              }`}
            >
              <Target size={20} /> {t('strategy.title') || 'SEO Strategy & Calendar'}
            </button>
            <button
              onClick={() => { setView('designmind'); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all text-slate-400 hover:text-white hover:bg-white/5 ${
                view === 'designmind' ? 'bg-indigo-600 text-white' : ''
              }`}
            >
              <LayoutGrid size={20} /> {t('designmind.title') || 'DesignMind AI - Website Mockup Generator'}
            </button>
            <button onClick={() => { setView('seo-guide'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${view === 'seo-guide' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
              <BookOpen size={20} /> {t('seoGuide.title') || 'SEO & AEO Master Guide'}
            </button>
            <button
              onClick={() => {
                setView('consulting');
                setSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${
                view === 'consulting' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <Calendar size={20} />{' '}
              {(() => {
                if (currentUser?.role === 'admin') {
                  const adminTitle = t('consulting.adminTitle');
                  return adminTitle !== 'consulting.adminTitle' ? adminTitle : 'Manage Bookings';
                } else {
                  const title = t('consulting.title');
                  return title !== 'consulting.title' ? title : 'Book Consulting Session';
                }
              })()}
            </button>
            <div className="px-4 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 mt-4">{(() => {
              const section = t('tools.reportsSection');
              return section && section !== 'tools.reportsSection' ? section : 'REPORTS';
            })()}</div>
            <button onClick={() => { setView('reporting'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${view === 'reporting' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
              <FileText size={20} /> {t('reporting.title') || 'reporting.title'}
            </button>
            <div className="px-4 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 mt-4">{t('common.profile') || 'PROFILE'}</div>
            <button onClick={() => { setView('profile'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${view === 'profile' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
              <UserIconLucide size={20} /> {t('common.profile')}
            </button>

            {currentUser?.role === 'admin' && (
              <>
                <div className="px-4 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 mt-4">{t('admin.title') || 'ADMIN'}</div>
                <button onClick={() => { setView('admin'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${view === 'admin' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
                  <Server size={20} /> {t('admin.title')}
                </button>
                <button onClick={() => { setShowErrorLogViewer(true); setSidebarOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all text-slate-400 hover:text-white hover:bg-white/5">
                  <AlertCircle size={20} /> Error Logs (Ctrl+Shift+L)
                </button>
              </>
            )}
          </nav>
          
          {/* Background Tasks Indicator */}
          {backgroundTasks.size > 0 && (
            <div className="p-4 border-t border-white/5 flex-shrink-0">
              <div className="text-xs text-slate-500 uppercase font-bold mb-2">Background Tasks</div>
              {Array.from(backgroundTasks.values()).filter(t => t.status === 'running').map(task => (
                <div key={task.id} className="flex items-center gap-2 text-xs text-slate-400 mb-1">
                  <div className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse"></div>
                  <span className="truncate">{task.message}</span>
        </div>
              ))}
            </div>
          )}

        {/* Sidebar Footer - Fixed */}
        <div className="p-6 flex-shrink-0 border-t border-white/5 bg-slate-900/50">
           <button onClick={handleLogout} className="flex items-center gap-3 text-red-400 hover:text-red-300 px-2 py-2 w-full transition-colors text-sm font-medium">
             <LogOut size={18} /> {t('common.logout')}
           </button>
        </div>
      </aside>
      )}

      {/* MOBILE OVERLAY */}
      {sidebarOpen && shouldShowSidebar && <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setSidebarOpen(false)}></div>}

      {/* CONTENT AREA */}
      <main className={`${shouldShowSidebar ? 'flex-1 h-screen' : 'flex-1 min-h-[calc(100vh-8rem)]'} overflow-y-auto relative ${isLegalPage ? 'w-full' : ''}`}>
         {shouldShowSidebar && (
         <header className="sticky top-0 z-30 bg-[#0f172a]/80 backdrop-blur-md border-b border-white/5 px-4 md:px-6 h-16 flex items-center justify-between gap-2" style={{ position: 'sticky', top: 0, left: 0, right: 0, width: '100%', display: 'flex' }}>
           <div className="flex items-center gap-3 md:gap-4 min-w-0 flex-1">
              <button className="md:hidden text-white p-2 hover:bg-white/5 rounded-lg transition-colors flex-shrink-0" onClick={() => setSidebarOpen(true)}>
                <Menu size={24}/>
              </button>
              <h1 className="text-base md:text-lg font-bold text-white capitalize truncate">
                {view === 'tools'
                  ? activeModule === 'audit'
                    ? t('tools.siteAudit')
                    : activeModule === 'keywords'
                    ? t('tools.keywordResearch')
                    : activeModule === 'competitors'
                    ? t('tools.competitorAnalysis')
                    : t('tools.roiCalculator')
                  : view === 'strategy'
                  ? t('strategy.title') || 'SEO Strategy & Calendar'
                  : view}
              </h1>
           </div>
           <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
             {currentUser?.credits === 0 && <span className="text-red-500 text-xs font-bold animate-pulse hidden sm:inline">{t('common.lowCredits')}</span>}
             {/* Language Switcher Button */}
             {currentUser && (
               <button
                 onClick={() => setShowLanguageModal(true)}
                 className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-2 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700 hover:border-indigo-500/50 transition-all text-slate-300 hover:text-white group"
                 title={`Change Language & Preferences (Current: ${currentUser.preferredLanguage || 'English'})`}
               >
                 <Globe size={18} className="text-indigo-400 group-hover:text-indigo-300 flex-shrink-0" />
                 <span className="text-xs font-medium hidden sm:inline">
                   {currentUser.preferredLanguage === 'Darija/French' ? 'Darija/FR' : currentUser.preferredLanguage || 'English'}
                 </span>
                 <span className="text-[10px] text-slate-500 hidden md:inline">
                   / {currentUser.uiLanguage || 'EN'}
                 </span>
               </button>
             )}
             <button
               onClick={() => setView('profile')}
               className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 flex items-center justify-center flex-shrink-0 transition-all hover:scale-110 cursor-pointer"
               title={t('common.profile') || 'View Profile'}
             >
               <UserIcon size={16} className="text-white"/>
             </button>
           </div>
         </header>
         )}

         <div className="p-4 md:p-6 max-w-7xl mx-auto">
            {/* VIEW: AI TOOLS */}
            {view === 'ai-tools' && (
              <AITools />
            )}

            {/* VIEW: SEO GUIDE */}
            {view === 'seo-guide' && (
              currentUser && currentUser.credits === 0 && currentUser.plan === 'free' ? (
                <ToolLocked 
                  toolName={t('seoGuide.title') || 'SEO & AEO Master Guide'} 
                  onUpgrade={() => setShowPaywall(true)} 
                />
              ) : (
                <SEOGuide />
              )
            )}

            {/* VIEW: SEO RESOURCES */}
            {view === 'seo-resources' && currentUser && (
              currentUser.credits === 0 && currentUser.plan === 'free' ? (
                <ToolLocked 
                  toolName={t('seoResources.title') || 'SEO Resources & Plugins'} 
                  onUpgrade={() => setShowPaywall(true)} 
                />
              ) : (
                <SEOResources currentUser={currentUser} />
              )
            )}

            {/* VIEW: CONSULTING BOOKING (User View) */}
            {view === 'consulting' && currentUser && currentUser.role !== 'admin' && (
              <ClientBooking currentUser={currentUser} />
            )}

            {/* VIEW: CONSULTING ADMIN DASHBOARD */}
            {view === 'consulting' && currentUser && currentUser.role === 'admin' && (
              <AdminConsultingDashboard currentUser={currentUser} />
            )}

            {view === 'backlinks' && currentUser && (
              <BacklinkBuilder currentUser={currentUser} />
            )}

            {view === 'gsc-analyzer' && currentUser && (
              <GSCAnalyzer />
            )}

            {view === 'speed-optimizer' && currentUser && (
              <SpeedOptimizer />
            )}

            {/* VIEW: PROFILE / REPORTING / STRATEGY */}
            {view === 'reporting' && currentUser && (
              currentUser.credits === 0 && currentUser.plan === 'free' ? (
                <ToolLocked 
                  toolName={t('reporting.title') || 'SEO Reports & Analytics'} 
                  onUpgrade={() => setShowPaywall(true)} 
                />
              ) : (
                <Reporting currentUser={currentUser} />
              )
            )}

            {view === 'profile' && currentUser && (
              <Profile 
                user={currentUser} 
                onUpdate={(updatedUser) => {
                  setCurrentUser(updatedUser);
                  localStorage.setItem('orca_user', JSON.stringify(updatedUser));
                }}
              />
            )}

            {view === 'strategy' && currentUser && (
              currentUser.credits === 0 && currentUser.plan === 'free' ? (
                <ToolLocked 
                  toolName={t('strategy.title') || 'SEO Strategy & Calendar'} 
                  onUpgrade={() => setShowPaywall(true)} 
                />
              ) : (
                <SeoStrategyPlanner currentUser={currentUser} />
              )
            )}

            {view === 'designmind' && currentUser && (
              <DesignMindAI currentUser={currentUser} />
            )}

            {/* VIEW: LEGAL PAGES */}
            {view === 'privacy-policy' && (
              <PrivacyPolicy onBack={() => setView('landing')} />
            )}

            {view === 'terms-of-service' && (
              <TermsOfService onBack={() => setView('landing')} />
            )}

            {view === 'refund-policy' && (
              <RefundPolicy onBack={() => setView('landing')} />
            )}

            {view === 'shipping-policy' && (
              <ShippingPolicy onBack={() => setView('landing')} />
            )}

            {view === 'contact' && (
              <ContactPage onBack={() => setView('landing')} />
            )}

            {/* VIEW: NEW SEO PAGES */}
            {view === 'services' && (
              <ServicesPage onBack={() => setView('landing')} onNavigate={(page) => setView(page as ViewType)} />
            )}

            {view === 'advantages' && (
              <AdvantagesPage onBack={() => setView('landing')} onNavigate={(page) => setView(page as ViewType)} />
            )}

            {view === 'how-to-use' && (
              <HowToUsePage onBack={() => setView('landing')} onNavigate={(page) => setView(page as ViewType)} />
            )}

            {view === 'faq' && (
              <FAQPage onBack={() => setView('landing')} />
            )}

            {/* VIEW: 404 Not Found - Catch all unknown views */}
            {!['landing', 'dashboard', 'tools', 'admin', 'profile', 'seo-guide', 'ai-tools', 'reporting', 'strategy', 'rank-tracking', 'jsonld', 'gsc-analyzer', 'speed-optimizer', 'designmind', 'seo-resources', 'backlinks', 'consulting', 'privacy-policy', 'terms-of-service', 'refund-policy', 'shipping-policy', 'contact', 'services', 'advantages', 'how-to-use', 'faq'].includes(view) && (
              <NotFoundPage 
                onNavigate={(page) => setView(page as ViewType)}
                onBack={() => setView('landing')}
              />
            )}

            {/* VIEW: DASHBOARD */}
            {view === 'dashboard' && currentUser && (
              <div className="animate-fadeIn space-y-8">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h1 className="text-4xl font-bold text-white mb-2">
                      {t('dashboard.welcomeBack', { name: currentUser?.name?.split(' ')[0] || 'User' })}
                    </h1>
                    <p className="text-slate-400 text-lg">
                      {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                    </div>
                  {currentUser && (
                    <div className="flex items-center gap-4">
                      <div className="bg-slate-800/50 border border-slate-700 rounded-xl px-6 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${currentUser.credits > 10 ? 'bg-emerald-500/20' : currentUser.credits > 0 ? 'bg-yellow-500/20' : 'bg-red-500/20'}`}>
                            <Zap className={`w-5 h-5 ${currentUser.credits > 10 ? 'text-emerald-400' : currentUser.credits > 0 ? 'text-yellow-400' : 'text-red-400'}`} />
                    </div>
                          <div>
                            <div className="text-xs text-slate-400 uppercase tracking-wider">Credits</div>
                            <div className={`text-2xl font-bold ${currentUser.credits > 10 ? 'text-emerald-400' : currentUser.credits > 0 ? 'text-yellow-400' : 'text-red-400'}`}>
                              {currentUser.credits}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-slate-800/50 border border-slate-700 rounded-xl px-6 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${currentUser.plan !== 'free' ? 'bg-indigo-500/20' : 'bg-slate-500/20'}`}>
                            <Star className={`w-5 h-5 ${currentUser.plan !== 'free' ? 'text-indigo-400' : 'text-slate-400'}`} />
                          </div>
                          <div>
                            <div className="text-xs text-slate-400 uppercase tracking-wider">Plan</div>
                            <div className="text-lg font-bold text-white capitalize">{currentUser.plan}</div>
                          </div>
                    </div>
                 </div>
                    </div>
                  )}
                </div>

                {/* Show verification warning if email not verified (non-admin only) */}
                {currentUser && currentUser.role !== 'admin' && !currentUser.emailVerified && (
                  <div className="bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border-2 border-yellow-500/50 rounded-2xl p-6 animate-fadeIn backdrop-blur-sm">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-yellow-500/20 rounded-xl">
                        <AlertCircle className="w-6 h-6 text-yellow-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-yellow-400 mb-2">{t('verification.title')}</h3>
                        <p className="text-slate-300 mb-4">
                          {t('verification.message')}
                        </p>
                        <button
                          onClick={() => {
                            setShowVerificationModal(true);
                            setPendingVerificationEmail(currentUser.email);
                          }}
                          className="bg-yellow-500 hover:bg-yellow-400 text-slate-900 px-6 py-2.5 rounded-lg font-semibold transition-colors flex items-center gap-2"
                        >
                          <CheckCircle2 size={18} />
                          {t('verification.verifyNow')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Quick Stats */}
                {currentUser && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-indigo-500/10 to-indigo-600/5 border border-indigo-500/20 rounded-2xl p-6 hover:border-indigo-500/40 transition-all group cursor-pointer" onClick={() => setView('tools')}>
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-indigo-500/20 rounded-xl group-hover:bg-indigo-500/30 transition-colors">
                          <Activity className="w-6 h-6 text-indigo-400" />
                        </div>
                        <ArrowRight className="w-5 h-5 text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <div className="text-3xl font-bold text-white mb-1">Ready</div>
                      <div className="text-sm text-slate-400">Start analyzing</div>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 rounded-2xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-emerald-500/20 rounded-xl">
                          <CreditCard className="w-6 h-6 text-emerald-400" />
                        </div>
                      </div>
                      <div className="text-3xl font-bold text-white mb-1">{currentUser.credits}</div>
                      <div className="text-sm text-slate-400">Available credits</div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20 rounded-2xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-purple-500/20 rounded-xl">
                          <FileText className="w-6 h-6 text-purple-400" />
                        </div>
                      </div>
                      <div className="text-3xl font-bold text-white mb-1">—</div>
                      <div className="text-sm text-slate-400">Reports generated</div>
                    </div>
                    <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-2xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-blue-500/20 rounded-xl">
                          <TrendingUp className="w-6 h-6 text-blue-400" />
                        </div>
                      </div>
                      <div className="text-3xl font-bold text-white mb-1">{currentUser.plan === 'free' ? 'Free' : 'Pro'}</div>
                      <div className="text-sm text-slate-400">Current plan</div>
                    </div>
                  </div>
                )}

                {/* Main Tools Grid */}
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                      <Sparkles className="w-6 h-6 text-indigo-400" />
                      SEO Tools
                    </h2>
                    <button
                      onClick={() => setView('seo-guide')}
                      className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-2"
                    >
                      <BookOpen size={16} />
                      Learn SEO
                    </button>
                  </div>
                 <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div 
                      onClick={() => {setView('tools'); setActiveModule('audit')}} 
                      className="group relative bg-gradient-to-br from-indigo-900/40 via-indigo-900/20 to-slate-900/80 border border-indigo-500/30 rounded-2xl p-8 cursor-pointer hover:scale-[1.02] hover:border-indigo-500/50 transition-all duration-300 overflow-hidden"
                    >
                      <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-3xl -mr-16 -mt-16 group-hover:bg-indigo-500/20 transition-colors"></div>
                      <div className="relative z-10">
                        <div className="p-4 bg-indigo-500/20 rounded-2xl w-fit mb-6 group-hover:bg-indigo-500/30 transition-colors">
                          <ShieldCheck className="w-8 h-8 text-indigo-400" />
                    </div>
                        <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-indigo-300 transition-colors">
                          {t('dashboard.siteAudit')}
                        </h3>
                        <p className="text-slate-400 mb-4 leading-relaxed">
                          {t('dashboard.siteAuditDesc')}
                        </p>
                        <div className="flex items-center gap-2 text-indigo-400 text-sm font-medium">
                          <span>Start audit</span>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                    </div>
                 </div>
                    <div 
                      onClick={() => {setView('tools'); setActiveModule('keywords')}} 
                      className="group relative bg-gradient-to-br from-emerald-900/40 via-emerald-900/20 to-slate-900/80 border border-emerald-500/30 rounded-2xl p-8 cursor-pointer hover:scale-[1.02] hover:border-emerald-500/50 transition-all duration-300 overflow-hidden"
                    >
                      <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl -mr-16 -mt-16 group-hover:bg-emerald-500/20 transition-colors"></div>
                      <div className="relative z-10">
                        <div className="p-4 bg-emerald-500/20 rounded-2xl w-fit mb-6 group-hover:bg-emerald-500/30 transition-colors">
                          <Search className="w-8 h-8 text-emerald-400" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-emerald-300 transition-colors">
                          {t('dashboard.keywordGenius')}
                        </h3>
                        <p className="text-slate-400 mb-4 leading-relaxed">
                          {t('dashboard.keywordGeniusDesc')}
                        </p>
                        <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium">
                          <span>Find keywords</span>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                    <div 
                      onClick={() => {setView('tools'); setActiveModule('competitors')}} 
                      className="group relative bg-gradient-to-br from-red-900/40 via-red-900/20 to-slate-900/80 border border-red-500/30 rounded-2xl p-8 cursor-pointer hover:scale-[1.02] hover:border-red-500/50 transition-all duration-300 overflow-hidden"
                    >
                      <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/10 rounded-full blur-3xl -mr-16 -mt-16 group-hover:bg-red-500/20 transition-colors"></div>
                      <div className="relative z-10">
                        <div className="p-4 bg-red-500/20 rounded-2xl w-fit mb-6 group-hover:bg-red-500/30 transition-colors">
                          <Swords className="w-8 h-8 text-red-400" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-red-300 transition-colors">
                          {t('dashboard.competitorSpy')}
                        </h3>
                        <p className="text-slate-400 mb-4 leading-relaxed">
                          {t('dashboard.competitorSpyDesc')}
                        </p>
                        <div className="flex items-center gap-2 text-red-400 text-sm font-medium">
                          <span>Analyze competitors</span>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                    <div 
                      onClick={() => {setView('rank-tracking')}} 
                      className="group relative bg-gradient-to-br from-blue-900/40 via-blue-900/20 to-slate-900/80 border border-blue-500/30 rounded-2xl p-8 cursor-pointer hover:scale-[1.02] hover:border-blue-500/50 transition-all duration-300 overflow-hidden"
                    >
                      <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl -mr-16 -mt-16 group-hover:bg-blue-500/20 transition-colors"></div>
                      <div className="relative z-10">
                        <div className="p-4 bg-blue-500/20 rounded-2xl w-fit mb-6 group-hover:bg-blue-500/30 transition-colors">
                          <BarChart2 className="w-8 h-8 text-blue-400" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-blue-300 transition-colors">
                          {(() => {
                            const text = t('dashboard.rankTracking');
                            return text && text !== 'dashboard.rankTracking' ? text : 'SERP Rank Tracking';
                          })()}
                        </h3>
                        <p className="text-slate-400 mb-4 leading-relaxed">
                          {(() => {
                            const text = t('dashboard.rankTrackingDesc');
                            return text && text !== 'dashboard.rankTrackingDesc' ? text : 'Track keyword rankings, monitor competitors, and discover ranking opportunities.';
                          })()}
                        </p>
                        <div className="flex items-center gap-2 text-blue-400 text-sm font-medium">
                          <span>{(() => {
                            const text = t('dashboard.trackRanks');
                            return text && text !== 'dashboard.trackRanks' ? text : 'Track ranks';
                          })()}</span>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Tools Row - All Tools */}
                <div>
                  <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                    <Zap className="w-6 h-6 text-yellow-400" />
                    {t('tools.resourcesSection') && t('tools.resourcesSection') !== 'tools.resourcesSection' ? t('tools.resourcesSection') : 'Resources & Tools'}
                  </h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                    {/* ROI Calculator */}
                    <div 
                      onClick={() => {setView('tools'); setActiveModule('roi')}} 
                      className="group relative bg-gradient-to-br from-purple-900/40 via-purple-900/20 to-slate-900/80 border border-purple-500/30 rounded-2xl p-6 cursor-pointer hover:scale-[1.02] hover:border-purple-500/50 transition-all duration-300 overflow-hidden"
                    >
                      <div className="absolute top-0 right-0 w-24 h-24 bg-purple-500/10 rounded-full blur-2xl -mr-12 -mt-12 group-hover:bg-purple-500/20 transition-colors"></div>
                      <div className="relative z-10">
                        <div className="p-3 bg-purple-500/20 rounded-xl w-fit mb-4 group-hover:bg-purple-500/30 transition-colors">
                          <Calculator className="w-6 h-6 text-purple-400" />
                        </div>
                        <h3 className="text-lg font-bold text-white mb-2 group-hover:text-purple-300 transition-colors">
                          {t('tools.roiCalculator')}
                        </h3>
                        <p className="text-slate-400 text-sm mb-3 leading-relaxed">
                          Estimate revenue potential
                        </p>
                        <div className="flex items-center gap-2 text-purple-400 text-xs font-medium">
                          <span>Open tool</span>
                          <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>

                    {/* AI Tools */}
                    <div 
                      onClick={() => setView('ai-tools')} 
                      className="group relative bg-gradient-to-br from-yellow-900/40 via-yellow-900/20 to-slate-900/80 border border-yellow-500/30 rounded-2xl p-6 cursor-pointer hover:scale-[1.02] hover:border-yellow-500/50 transition-all duration-300 overflow-hidden"
                    >
                      <div className="absolute top-0 right-0 w-24 h-24 bg-yellow-500/10 rounded-full blur-2xl -mr-12 -mt-12 group-hover:bg-yellow-500/20 transition-colors"></div>
                      <div className="relative z-10">
                        <div className="p-3 bg-yellow-500/20 rounded-xl w-fit mb-4 group-hover:bg-yellow-500/30 transition-colors">
                          <Zap className="w-6 h-6 text-yellow-400" />
                        </div>
                        <h3 className="text-lg font-bold text-white mb-2 group-hover:text-yellow-300 transition-colors">
                          {t('aiTools.title') || 'AI Tools'}
                        </h3>
                        <p className="text-slate-400 text-sm mb-3 leading-relaxed">
                          AI-powered SEO assistant
                        </p>
                        <div className="flex items-center gap-2 text-yellow-400 text-xs font-medium">
                          <span>Open tool</span>
                          <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>

                    {/* Reports */}
                    {currentUser && (currentUser.credits > 0 || currentUser.plan !== 'free') && (
                      <div 
                        onClick={() => setView('reporting')} 
                        className="group relative bg-gradient-to-br from-blue-900/40 via-blue-900/20 to-slate-900/80 border border-blue-500/30 rounded-2xl p-6 cursor-pointer hover:scale-[1.02] hover:border-blue-500/50 transition-all duration-300 overflow-hidden"
                      >
                        <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl -mr-12 -mt-12 group-hover:bg-blue-500/20 transition-colors"></div>
                        <div className="relative z-10">
                          <div className="p-3 bg-blue-500/20 rounded-xl w-fit mb-4 group-hover:bg-blue-500/30 transition-colors">
                            <FileText className="w-6 h-6 text-blue-400" />
                          </div>
                          <h3 className="text-lg font-bold text-white mb-2 group-hover:text-blue-300 transition-colors">
                            {t('reporting.title') && t('reporting.title') !== 'reporting.title' ? t('reporting.title') : 'Reports'}
                          </h3>
                          <p className="text-slate-400 text-sm mb-3 leading-relaxed">
                            View & download PDFs
                          </p>
                          <div className="flex items-center gap-2 text-blue-400 text-xs font-medium">
                            <span>Open tool</span>
                            <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* SEO Guide */}
                    {currentUser && (currentUser.credits > 0 || currentUser.plan !== 'free') && (
                      <div 
                        onClick={() => setView('seo-guide')} 
                        className="group relative bg-gradient-to-br from-green-900/40 via-green-900/20 to-slate-900/80 border border-green-500/30 rounded-2xl p-6 cursor-pointer hover:scale-[1.02] hover:border-green-500/50 transition-all duration-300 overflow-hidden"
                      >
                        <div className="absolute top-0 right-0 w-24 h-24 bg-green-500/10 rounded-full blur-2xl -mr-12 -mt-12 group-hover:bg-green-500/20 transition-colors"></div>
                        <div className="relative z-10">
                          <div className="p-3 bg-green-500/20 rounded-xl w-fit mb-4 group-hover:bg-green-500/30 transition-colors">
                            <BookOpen className="w-6 h-6 text-green-400" />
                          </div>
                          <h3 className="text-lg font-bold text-white mb-2 group-hover:text-green-300 transition-colors">
                            {t('seoGuide.title') || 'SEO Guide'}
                          </h3>
                          <p className="text-slate-400 text-sm mb-3 leading-relaxed">
                            Learn SEO best practices
                          </p>
                          <div className="flex items-center gap-2 text-green-400 text-xs font-medium">
                            <span>Open tool</span>
                            <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* SEO Strategy & Calendar */}
                    {currentUser && (currentUser.credits > 0 || currentUser.plan !== 'free') && (
                      <div 
                        onClick={() => setView('strategy')} 
                        className="group relative bg-gradient-to-br from-pink-900/40 via-pink-900/20 to-slate-900/80 border border-pink-500/30 rounded-2xl p-6 cursor-pointer hover:scale-[1.02] hover:border-pink-500/50 transition-all duration-300 overflow-hidden"
                      >
                        <div className="absolute top-0 right-0 w-24 h-24 bg-pink-500/10 rounded-full blur-2xl -mr-12 -mt-12 group-hover:bg-pink-500/20 transition-colors"></div>
                        <div className="relative z-10">
                          <div className="p-3 bg-pink-500/20 rounded-xl w-fit mb-4 group-hover:bg-pink-500/30 transition-colors">
                            <Target className="w-6 h-6 text-pink-400" />
                          </div>
                          <h3 className="text-lg font-bold text-white mb-2 group-hover:text-pink-300 transition-colors">
                            {t('strategy.title') || 'SEO Strategy'}
                          </h3>
                          <p className="text-slate-400 text-sm mb-3 leading-relaxed">
                            Content calendar & planning
                          </p>
                          <div className="flex items-center gap-2 text-pink-400 text-xs font-medium">
                            <span>Open tool</span>
                            <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* DesignMind AI */}
                    <div 
                      onClick={() => setView('designmind')} 
                      className="group relative bg-gradient-to-br from-cyan-900/40 via-cyan-900/20 to-slate-900/80 border border-cyan-500/30 rounded-2xl p-6 cursor-pointer hover:scale-[1.02] hover:border-cyan-500/50 transition-all duration-300 overflow-hidden"
                    >
                      <div className="absolute top-0 right-0 w-24 h-24 bg-cyan-500/10 rounded-full blur-2xl -mr-12 -mt-12 group-hover:bg-cyan-500/20 transition-colors"></div>
                      <div className="relative z-10">
                        <div className="p-3 bg-cyan-500/20 rounded-xl w-fit mb-4 group-hover:bg-cyan-500/30 transition-colors">
                          <LayoutGrid className="w-6 h-6 text-cyan-400" />
                        </div>
                        <h3 className="text-lg font-bold text-white mb-2 group-hover:text-cyan-300 transition-colors">
                          {t('designmind.title') || 'DesignMind AI'}
                        </h3>
                        <p className="text-slate-400 text-sm mb-3 leading-relaxed">
                          Website mockup generator
                        </p>
                        <div className="flex items-center gap-2 text-cyan-400 text-xs font-medium">
                          <span>Open tool</span>
                          <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>

                    {/* SEO Resources & Plugins */}
                    {currentUser && (currentUser.credits > 0 || currentUser.plan !== 'free') && (
                      <div 
                        onClick={() => setView('seo-resources')} 
                        className="group relative bg-gradient-to-br from-orange-900/40 via-orange-900/20 to-slate-900/80 border border-orange-500/30 rounded-2xl p-6 cursor-pointer hover:scale-[1.02] hover:border-orange-500/50 transition-all duration-300 overflow-hidden"
                      >
                        <div className="absolute top-0 right-0 w-24 h-24 bg-orange-500/10 rounded-full blur-2xl -mr-12 -mt-12 group-hover:bg-orange-500/20 transition-colors"></div>
                        <div className="relative z-10">
                          <div className="p-3 bg-orange-500/20 rounded-xl w-fit mb-4 group-hover:bg-orange-500/30 transition-colors">
                            <Package className="w-6 h-6 text-orange-400" />
                          </div>
                          <h3 className="text-lg font-bold text-white mb-2 group-hover:text-orange-300 transition-colors">
                            {t('seoResources.title') || 'SEO Resources'}
                          </h3>
                          <p className="text-slate-400 text-sm mb-3 leading-relaxed">
                            WordPress & Shopify plugins
                          </p>
                          <div className="flex items-center gap-2 text-orange-400 text-xs font-medium">
                            <span>Open tool</span>
                            <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Backlink Builder */}
                    {currentUser && (currentUser.credits > 0 || currentUser.plan !== 'free') && (
                      <div 
                        onClick={() => setView('backlinks')} 
                        className="group relative bg-gradient-to-br from-violet-900/40 via-violet-900/20 to-slate-900/80 border border-violet-500/30 rounded-2xl p-6 cursor-pointer hover:scale-[1.02] hover:border-violet-500/50 transition-all duration-300 overflow-hidden"
                      >
                        <div className="absolute top-0 right-0 w-24 h-24 bg-violet-500/10 rounded-full blur-2xl -mr-12 -mt-12 group-hover:bg-violet-500/20 transition-colors"></div>
                        <div className="relative z-10">
                          <div className="p-3 bg-violet-500/20 rounded-xl w-fit mb-4 group-hover:bg-violet-500/30 transition-colors">
                            <Link2 className="w-6 h-6 text-violet-400" />
                          </div>
                          <h3 className="text-lg font-bold text-white mb-2 group-hover:text-violet-300 transition-colors">
                            {t('backlinks.title') || 'Backlink Builder'}
                          </h3>
                          <p className="text-slate-400 text-sm mb-3 leading-relaxed">
                            301 redirect guide & resources
                          </p>
                          <div className="flex items-center gap-2 text-violet-400 text-xs font-medium">
                            <span>Open tool</span>
                            <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* GSC Analyzer */}
                    {currentUser && (currentUser.credits > 0 || currentUser.plan !== 'free') && (
                      <div 
                        onClick={() => setView('gsc-analyzer')} 
                        className="group relative bg-gradient-to-br from-teal-900/40 via-teal-900/20 to-slate-900/80 border border-teal-500/30 rounded-2xl p-6 cursor-pointer hover:scale-[1.02] hover:border-teal-500/50 transition-all duration-300 overflow-hidden"
                      >
                        <div className="absolute top-0 right-0 w-24 h-24 bg-teal-500/10 rounded-full blur-2xl -mr-12 -mt-12 group-hover:bg-teal-500/20 transition-colors"></div>
                        <div className="relative z-10">
                          <div className="p-3 bg-teal-500/20 rounded-xl w-fit mb-4 group-hover:bg-teal-500/30 transition-colors">
                            <BarChart3 className="w-6 h-6 text-teal-400" />
                          </div>
                          <h3 className="text-lg font-bold text-white mb-2 group-hover:text-teal-300 transition-colors">
                            {(() => {
                              const text = t('gsc.title');
                              return text && text !== 'gsc.title' ? text : 'GSC Analyzer';
                            })()}
                          </h3>
                          <p className="text-slate-400 text-sm mb-3 leading-relaxed">
                            {(() => {
                              const text = t('gsc.subtitle');
                              return text && text !== 'gsc.subtitle' ? text : 'Analyze your GSC data with AI';
                            })()}
                          </p>
                          <div className="flex items-center gap-2 text-teal-400 text-xs font-medium">
                            <span>Open tool</span>
                            <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Speed Optimizer */}
                    {currentUser && (currentUser.credits > 0 || currentUser.plan !== 'free') && (
                      <div 
                        onClick={() => setView('speed-optimizer')} 
                        className="group relative bg-gradient-to-br from-yellow-900/40 via-orange-900/20 to-slate-900/80 border border-yellow-500/30 rounded-2xl p-6 cursor-pointer hover:scale-[1.02] hover:border-yellow-500/50 transition-all duration-300 overflow-hidden"
                      >
                        <div className="absolute top-0 right-0 w-24 h-24 bg-yellow-500/10 rounded-full blur-2xl -mr-12 -mt-12 group-hover:bg-yellow-500/20 transition-colors"></div>
                        <div className="relative z-10">
                          <div className="p-3 bg-yellow-500/20 rounded-xl w-fit mb-4 group-hover:bg-yellow-500/30 transition-colors">
                            <Zap className="w-6 h-6 text-yellow-400" />
                          </div>
                          <h3 className="text-lg font-bold text-white mb-2 group-hover:text-yellow-300 transition-colors">
                            {(() => {
                              const text = t('speedOptimizer.title');
                              return text && text !== 'speedOptimizer.title' ? text : 'Speed Optimizer';
                            })()}
                          </h3>
                          <p className="text-slate-400 text-sm mb-3 leading-relaxed">
                            {(() => {
                              const text = t('speedOptimizer.cardDescription');
                              return text && text !== 'speedOptimizer.cardDescription' ? text : 'Analyze website speed and Core Web Vitals.';
                            })()}
                          </p>
                          <div className="flex items-center gap-2 text-yellow-400 text-xs font-medium">
                            <span>{t('common.openTool') || 'Open tool'}</span>
                            <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Consulting Booking */}
                    <div 
                      onClick={() => setView('consulting')} 
                      className="group relative bg-gradient-to-br from-rose-900/40 via-rose-900/20 to-slate-900/80 border border-rose-500/30 rounded-2xl p-6 cursor-pointer hover:scale-[1.02] hover:border-rose-500/50 transition-all duration-300 overflow-hidden"
                    >
                      <div className="absolute top-0 right-0 w-24 h-24 bg-rose-500/10 rounded-full blur-2xl -mr-12 -mt-12 group-hover:bg-rose-500/20 transition-colors"></div>
                      <div className="relative z-10">
                        <div className="p-3 bg-rose-500/20 rounded-xl w-fit mb-4 group-hover:bg-rose-500/30 transition-colors">
                          <Calendar className="w-6 h-6 text-rose-400" />
                        </div>
                        <h3 className="text-lg font-bold text-white mb-2 group-hover:text-rose-300 transition-colors">
                          {currentUser?.role === 'admin' 
                            ? (t('consulting.adminTitle') && t('consulting.adminTitle') !== 'consulting.adminTitle' ? t('consulting.adminTitle') : 'Manage Bookings')
                            : (t('consulting.title') && t('consulting.title') !== 'consulting.title' ? t('consulting.title') : 'Book Consulting')
                          }
                        </h3>
                        <p className="text-slate-400 text-sm mb-3 leading-relaxed">
                          {currentUser?.role === 'admin' ? 'Manage all bookings' : 'Book a session with our expert'}
                        </p>
                        <div className="flex items-center gap-2 text-rose-400 text-xs font-medium">
                          <span>Open tool</span>
                          <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Tips / Getting Started */}
                {currentUser && currentUser.credits > 0 && (
                  <div className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 border border-indigo-500/20 rounded-2xl p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-indigo-500/20 rounded-xl">
                        <Rocket className="w-6 h-6 text-indigo-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-white mb-2">Ready to get started?</h3>
                        <p className="text-slate-300 mb-4">
                          Run your first SEO audit to discover issues and opportunities. Our AI-powered analysis will give you actionable insights to improve your website's search engine performance.
                        </p>
                        <button
                          onClick={() => {setView('tools'); setActiveModule('audit')}}
                          className="bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-2.5 rounded-lg font-semibold transition-colors flex items-center gap-2"
                        >
                          <ShieldCheck size={18} />
                          Start Your First Audit
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Low Credits Warning */}
                {currentUser && currentUser.credits <= 5 && currentUser.credits > 0 && (
                  <div className="bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border border-yellow-500/30 rounded-2xl p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-yellow-500/20 rounded-xl">
                        <AlertCircle className="w-6 h-6 text-yellow-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-yellow-400 mb-1">Running low on credits</h3>
                        <p className="text-slate-300 text-sm">
                          You have {currentUser.credits} credit{currentUser.credits !== 1 ? 's' : ''} remaining. Upgrade your plan for unlimited access.
                        </p>
                      </div>
                      <button
                        onClick={() => setShowPaywall(true)}
                        className="bg-yellow-500 hover:bg-yellow-400 text-slate-900 px-6 py-2.5 rounded-lg font-semibold transition-colors whitespace-nowrap"
                      >
                        Upgrade Plan
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* VIEW: TOOLS */}
            {view === 'tools' && (
              <div className="animate-slideUp">
                 {activeModule === 'audit' && (
                   <div>
                     <h2 className="text-2xl font-bold text-white mb-6">{t('tools.siteAudit')}</h2>
                     {!report && !auditLoading && !partialReport ? (
                        <InputForm onAnalyze={(u, h) => runAudit(u, h)} isLoading={auditLoading} loadingMessage={auditLoadingMessage || "Analysing (1 Credit)..."} />
                     ) : (
                        <div className="space-y-6">
                          {/* Progress Steps - Time-based calculation */}
                          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 space-y-4">
                            <h3 className="text-lg font-semibold text-white mb-4">{t('auditProgress.overallProgress') || 'Audit Progress'}</h3>
                            
                            {['extraction', 'enhanced', 'ai-analysis', 'link-performance'].map((step, index) => {
                              const progress = auditProgress.get(step);
                              const isRunning = progress?.status === 'running';
                              const isCompleted = progress?.status === 'completed';
                              const isSkipped = progress?.status === 'skipped';
                              const isWaiting = progress?.status === 'waiting' || !progress;
                              
                              // Calculate progress based on time remaining
                              let progressPercent = 0;
                              if (isCompleted) {
                                progressPercent = 100;
                              } else if (isRunning) {
                                // Use actual progress from backend if available
                                if (progress?.progress !== undefined && progress.progress > 0) {
                                  progressPercent = progress.progress;
                                } else if (progress?.estimatedTimeRemaining && progress?.duration) {
                                  // Calculate based on time: (elapsed / (elapsed + remaining)) * 100
                                  const elapsed = progress.duration;
                                  const remaining = progress.estimatedTimeRemaining;
                                  progressPercent = Math.min(95, Math.max(5, (elapsed / (elapsed + remaining)) * 100));
                                } else if (progress?.estimatedTimeRemaining) {
                                  // Estimate based on typical step duration
                                  const stepDurations = [5000, 3000, 15000, 10000]; // ms
                                  const typicalDuration = stepDurations[index] || 5000;
                                  const remaining = progress.estimatedTimeRemaining;
                                  const elapsed = typicalDuration - remaining;
                                  progressPercent = Math.min(95, Math.max(5, (elapsed / typicalDuration) * 100));
                                } else {
                                  // Fallback: animate from 0 to 95% smoothly
                                  const stepWeights = [20, 15, 50, 15];
                                  progressPercent = Math.min(95, stepWeights[index] || 0);
                                }
                              }
                              
                              // Calculate estimated time remaining
                              const estimatedTime = progress?.estimatedTimeRemaining 
                                ? Math.ceil(progress.estimatedTimeRemaining / 1000) 
                                : null;
                              const duration = progress?.duration 
                                ? (progress.duration / 1000).toFixed(1) 
                                : null;
                              
                              return (
                                <div key={step} className="flex items-start gap-4 p-4 rounded-lg bg-slate-900/50 border border-slate-700/50 hover:border-indigo-500/50 transition-all">
                                  <div className="flex-shrink-0 mt-1">
                                    {isCompleted ? (
                                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-green-500/50 animate-scale-in">
                                        <CheckCircle className="text-white w-5 h-5" />
                                      </div>
                                    ) : isRunning ? (
                                      <div className="relative w-10 h-10">
                                        <svg className="w-10 h-10 transform -rotate-90">
                                          <circle
                                            className="text-slate-700"
                                            strokeWidth="3"
                                            stroke="currentColor"
                                            fill="transparent"
                                            r="16"
                                            cx="20"
                                            cy="20"
                                          />
                                          <circle
                                            className="text-indigo-500"
                                            strokeWidth="3"
                                            strokeDasharray={`${2 * Math.PI * 16}`}
                                            strokeDashoffset={`${2 * Math.PI * 16 * (1 - progressPercent / 100)}`}
                                            strokeLinecap="round"
                                            stroke="currentColor"
                                            fill="transparent"
                                            r="16"
                                            cx="20"
                                            cy="20"
                                            style={{ transition: 'stroke-dashoffset 0.3s ease' }}
                                          />
                                        </svg>
                                        <div className="absolute inset-0 flex items-center justify-center">
                                          <span className="text-xs font-bold text-indigo-400">{Math.round(progressPercent)}%</span>
                                        </div>
                                      </div>
                                    ) : isSkipped ? (
                                      <div className="w-10 h-10 rounded-full bg-slate-600 flex items-center justify-center">
                                        <span className="text-slate-400 text-xs">-</span>
                                      </div>
                                    ) : (
                                      <div className="w-10 h-10 rounded-full border-2 border-slate-600 flex items-center justify-center">
                                        <span className="text-slate-500 text-xs">0%</span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between mb-2">
                                      <h4 className="font-semibold text-white text-base">{progress?.title || step.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</h4>
                                      <div className="flex items-center gap-2">
                                        {isRunning && estimatedTime && (
                                          <span className="text-xs text-indigo-400 font-semibold animate-pulse">
                                            ⏱️ ~{estimatedTime}s
                                          </span>
                                        )}
                                        {isCompleted && duration && (
                                          <span className="text-xs text-green-400 font-semibold">
                                            ✅ {duration}s
                                          </span>
                                        )}
                                        {isSkipped && (
                                          <span className="text-xs text-slate-500 font-semibold">
                                            ⏭️ Skipped
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    {progress?.message && (
                                      <p className="text-sm text-slate-400 mb-2 animate-fadeIn">{progress.message}</p>
                                    )}
                                    {/* Animated progress bar for running steps */}
                                    {isRunning && (
                                      <div className="w-full bg-slate-700/50 rounded-full h-2.5 overflow-hidden shadow-inner">
                                        <div 
                                          className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 h-2.5 rounded-full transition-all duration-500 ease-out relative overflow-hidden"
                                          style={{ width: `${progressPercent}%` }}
                                        >
                                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
                                        </div>
                                      </div>
                                    )}
                                    {isCompleted && (
                                      <div className="w-full bg-slate-700/50 rounded-full h-2.5 overflow-hidden">
                                        <div className="bg-gradient-to-r from-green-500 to-emerald-500 h-2.5 rounded-full w-full"></div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                            
                            {/* Overall Progress with Real-Time Updates */}
                            <div className="mt-6 pt-4 border-t border-slate-700">
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-sm font-semibold text-slate-300">{t('auditProgress.overallProgress') || 'Overall Progress'}</span>
                                <div className="flex items-center gap-3">
                                  {(() => {
                                    const completed = Array.from(auditProgress.values()).filter(p => p.status === 'completed').length;
                                    const running = Array.from(auditProgress.values()).filter(p => p.status === 'running').length;
                                    const totalTime = Array.from(auditProgress.values())
                                      .filter(p => p.duration)
                                      .reduce((sum, p) => sum + (p.duration || 0), 0);
                                    const remainingTime = Array.from(auditProgress.values())
                                      .filter(p => p.status === 'running' && p.estimatedTimeRemaining)
                                      .reduce((sum, p) => sum + (p.estimatedTimeRemaining || 0), 0);
                                    
                                    if (running > 0 && remainingTime > 0) {
                                      return (
                                        <span className="text-xs text-indigo-400 font-semibold animate-pulse">
                                          ⏱️ ~{Math.ceil(remainingTime / 1000)}s remaining
                                        </span>
                                      );
                                    }
                                    if (completed === 4 && totalTime > 0) {
                                      return (
                                        <span className="text-xs text-green-400 font-semibold">
                                          ✅ Completed in {(totalTime / 1000).toFixed(1)}s
                                        </span>
                                      );
                                    }
                                    return null;
                                  })()}
                                  <span className="text-lg font-bold text-indigo-400">
                                    {(() => {
                                      // Calculate accurate overall progress
                                      const steps = [
                                        { key: 'extraction', weight: 25 },
                                        { key: 'enhanced', weight: 15 },
                                        { key: 'ai-analysis', weight: 30 },
                                        { key: 'link-performance', weight: 30 }
                                      ];
                                      
                                      let totalProgress = 0;
                                      let totalWeight = 0;
                                      
                                      steps.forEach(({ key, weight }) => {
                                        const progress = auditProgress.get(key);
                                        if (progress) {
                                          if (progress.status === 'completed' || progress.status === 'timeout' || progress.status === 'failed') {
                                            totalProgress += weight;
                                          } else if (progress.status === 'running' && progress.progress) {
                                            totalProgress += (progress.progress / 100) * weight;
                                          }
                                          totalWeight += weight;
                                        }
                                      });
                                      
                                      return totalWeight > 0 ? Math.round((totalProgress / totalWeight) * 100) : 0;
                                    })()}%
                                  </span>
                                </div>
                              </div>
                              <div className="w-full bg-slate-700/50 rounded-full h-4 overflow-hidden shadow-inner">
                                <div 
                                  className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 h-4 rounded-full transition-all duration-700 ease-out relative overflow-hidden"
                                  style={{ width: `${(() => {
                                    // Calculate accurate overall progress
                                    const steps = [
                                      { key: 'extraction', weight: 25 },
                                      { key: 'enhanced', weight: 15 },
                                      { key: 'ai-analysis', weight: 30 },
                                      { key: 'link-performance', weight: 30 }
                                    ];
                                    
                                    let totalProgress = 0;
                                    let totalWeight = 0;
                                    
                                    steps.forEach(({ key, weight }) => {
                                      const progress = auditProgress.get(key);
                                      if (progress) {
                                        if (progress.status === 'completed' || progress.status === 'timeout' || progress.status === 'failed') {
                                          totalProgress += weight;
                                        } else if (progress.status === 'running' && progress.progress) {
                                          totalProgress += (progress.progress / 100) * weight;
                                        }
                                        totalWeight += weight;
                                      }
                                    });
                                    
                                    return totalWeight > 0 ? Math.round((totalProgress / totalWeight) * 100) : 0;
                                  })()}%` }}
                                >
                                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer"></div>
                                </div>
                              </div>
                              <div className="flex justify-between mt-2 text-xs text-slate-500">
                                <span>{t('auditProgress.step') || 'Step'} {Array.from(auditProgress.values()).filter(p => p.status === 'completed').length} {t('auditProgress.of') || 'of'} 4</span>
                                <span>{Array.from(auditProgress.values()).filter(p => p.status === 'running').length > 0 ? (t('auditProgress.processing') || 'Processing...') : 'Almost done!'}</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Partial Report Display */}
                          {partialReport && (
                            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                              <h3 className="text-lg font-semibold text-white mb-4">{t('results.preliminaryResults') || 'Preliminary Results'}</h3>
                              <div className="space-y-4">
                                <div className="bg-slate-900/50 rounded-lg p-4">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-slate-400 text-sm">{t('results.overallScore') || 'Overall Score'}</span>
                                    <span className="text-2xl font-bold text-white">{partialReport.overallScore}/100</span>
                                  </div>
                                  {partialReport.summary && (
                                    <p className="text-slate-300 text-sm mt-2">{partialReport.summary}</p>
                                  )}
                                </div>
                                
                                {partialReport.categories && (
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {Object.entries(partialReport.categories).map(([category, data]: [string, any]) => (
                                      <div key={category} className="bg-slate-900/50 rounded-lg p-3">
                                        <div className="text-xs text-slate-400 mb-1">{category}</div>
                                        <div className="text-lg font-bold text-white">{data.score}/100</div>
                                        <div className="text-xs text-slate-500 mt-1">{data.totalIssues} issues</div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                {partialReport.issues && partialReport.issues.length > 0 && (
                                  <div className="mt-4">
                                    <h4 className="text-sm font-semibold text-white mb-2">Issues Found ({partialReport.issues.length})</h4>
                                    <div className="space-y-2 max-h-60 overflow-y-auto">
                                      {partialReport.issues.slice(0, 5).map((issue: any) => (
                                        <div key={issue.id} className="bg-slate-900/50 rounded p-2 text-sm">
                                          <div className="flex items-center gap-2">
                                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                              issue.severity === 'Critical' ? 'bg-red-500/20 text-red-400' :
                                              issue.severity === 'High' ? 'bg-orange-500/20 text-orange-400' :
                                              'bg-yellow-500/20 text-yellow-400'
                                            }`}>
                                              {issue.severity}
                                            </span>
                                            <span className="text-slate-300 flex-1 truncate">{issue.title}</span>
                                          </div>
                                        </div>
                                      ))}
                                      {partialReport.issues.length > 5 && (
                                        <p className="text-xs text-slate-500 text-center">+ {partialReport.issues.length - 5} more issues...</p>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Progressive Audit Display */}
                          <ProgressiveAuditDisplay
                            report={report}
                            partialReport={partialReport}
                            auditProgress={auditProgress}
                            isLoading={auditLoading}
                          />
                          
                          {/* Full Results Dashboard when complete */}
                          {report && !auditLoading && (
                            <ResultsDashboard 
                              report={report} 
                              onRescan={() => {
                                const url = report.url;
                                setReport(null);
                                setPartialReport(null);
                                setTimeout(() => runAudit(url, ''), 100);
                              }}
                              onNewScan={() => {
                                setReport(null);
                                setPartialReport(null);
                                setView('tools');
                                setActiveModule('audit');
                              }}
                            />
                          )}
                        </div>
                     )}
                   </div>
                 )}
                 {activeModule === 'keywords' && (
                   <div className="space-y-4">
                     {/* Target Market Selector */}
                     <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                       <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                         <Target size={16} className="text-indigo-400" />
                         {t('tools.targetMarket')}
                       </h3>
                       <div className="grid md:grid-cols-2 gap-4">
                         <div>
                           <label className="block text-xs text-slate-400 mb-2">{t('tools.country')}</label>
                           <select
                             value={projectConfig.country}
                             onChange={(e) => setProjectConfig({ ...projectConfig, country: e.target.value })}
                             className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2 text-sm"
                           >
                             <option value="Morocco">Morocco</option>
                             <option value="United States">United States</option>
                             <option value="United Kingdom">United Kingdom</option>
                             <option value="France">France</option>
                             <option value="Canada">Canada</option>
                             <option value="Germany">Germany</option>
                             <option value="Spain">Spain</option>
                             <option value="Italy">Italy</option>
                             <option value="Saudi Arabia">Saudi Arabia</option>
                             <option value="UAE">UAE</option>
                             <option value="Egypt">Egypt</option>
                             <option value="Algeria">Algeria</option>
                             <option value="Brazil">Brazil</option>
                             <option value="Mexico">Mexico</option>
                             <option value="Australia">Australia</option>
                             <option value="Japan">Japan</option>
                             <option value="South Korea">South Korea</option>
                             <option value="China">China</option>
                             <option value="India">India</option>
                           </select>
                         </div>
                         <div>
                           <label className="block text-xs text-slate-400 mb-2">{t('tools.language')}</label>
                           <select
                             value={projectConfig.language}
                             onChange={(e) => setProjectConfig({ ...projectConfig, language: e.target.value })}
                             className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2 text-sm"
                           >
                             <option value="English">English</option>
                             <option value="French">French</option>
                             <option value="Arabic">Arabic</option>
                             <option value="Spanish">Spanish</option>
                             <option value="German">German</option>
                             <option value="Italian">Italian</option>
                             <option value="Portuguese">Portuguese</option>
                             <option value="Dutch">Dutch</option>
                             <option value="Darija/French">Darija/French</option>
                             <option value="Chinese">Chinese</option>
                             <option value="Japanese">Japanese</option>
                             <option value="Korean">Korean</option>
                           </select>
                         </div>
                       </div>
                     </div>
                   <KeywordTool 
                     config={projectConfig} 
                     onRun={async (s, i) => { const r = await runKeywords(s, i); setKeywordResults(r || null); return r; }}
                     results={keywordResults}
                     loading={keywordsLoading}
                   />
                   </div>
                 )}
                 {activeModule === 'competitors' && (
                   <CompetitorTool 
                      onRun={async (m, c) => { const r = await runCompetitor(m, c); setCompAnalysis(r || null); return r; }}
                      analysis={compAnalysis}
                      loading={competitorLoading}
                   />
                 )}
                 {activeModule === 'roi' && (
                   <RoiCalculator language={projectConfig.language} />
                 )}
              </div>
            )}

            {/* VIEW: RANK TRACKING / SERP TRACKER */}
            {view === 'rank-tracking' && (
              <div className="animate-slideUp">
                <SerpRankTracker />
              </div>
            )}

            {/* VIEW: JSON-LD ANALYZER */}
            {view === 'jsonld' && (
              <div className="animate-slideUp">
                <JsonLdAnalyzer onBack={() => setView('tools')} />
              </div>
            )}

            {/* VIEW: ADMIN */}
            {view === 'admin' && currentUser?.role === 'admin' && <AdminPanel />}
            
            {/* ERROR LOG VIEWER */}
            {showErrorLogViewer && (
              <ErrorLogViewer onClose={() => setShowErrorLogViewer(false)} />
            )}
         </div>

         {/* FOOTER - Inside main so it scrolls with content */}
         {!isLandingPage && isLegalPage && (
           <CommonFooter 
             onNavigateToLegal={(page) => setView(page as ViewType)}
           />
         )}
      </main>

      {/* Rank Tracking Create Project Modal - Rendered at App level to escape overflow constraints */}
      {showRankTrackingModal && (
        <div 
          data-rank-tracking-modal="true"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowRankTrackingModal(false);
              setRankTrackingModalData({ projectName: '', domain: '', trackingFrequency: 'daily' });
            }
          }}
        >
          <div 
            className="bg-slate-800 rounded-xl p-6 border border-slate-700 max-w-md w-full mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-white">
                {(() => {
                  const text = t('rank_tracking.create_project');
                  return text && text !== 'rank_tracking.create_project' ? text : 'Create New Project';
                })()}
              </h2>
              <button
                onClick={() => {
                  setShowRankTrackingModal(false);
                  setRankTrackingModalData({ projectName: '', domain: '', trackingFrequency: 'daily' });
                }}
                className="text-slate-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              
              if (!rankTrackingModalData.projectName || !rankTrackingModalData.domain) {
                alert('Please fill in all required fields');
                return;
              }

              try {
                // No token check needed - cookies are sent automatically

                const response = await fetch('/api/rank-tracking/projects', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  credentials: 'include' as RequestCredentials,
                  body: JSON.stringify({
                    projectName: rankTrackingModalData.projectName,
                    domain: rankTrackingModalData.domain,
                    trackingFrequency: rankTrackingModalData.trackingFrequency,
                    keywords: [],
                    competitors: []
                  })
                });

                if (!response.ok) {
                  const errorData = await response.json().catch(() => ({ error: 'Failed to create project' }));
                  throw new Error(errorData.error || 'Failed to create project');
                }

                const data = await response.json();
                alert((() => {
                  const text = t('rank_tracking.project_created');
                  return text && text !== 'rank_tracking.project_created' ? text : 'Project created successfully!';
                })());
                
                // Close modal and reset form
                setShowRankTrackingModal(false);
                setRankTrackingModalData({ projectName: '', domain: '', trackingFrequency: 'daily' });
                
                // Trigger refresh in RankTrackingDashboard
                setRankTrackingRefreshKey(prev => prev + 1);
              } catch (error: any) {
                console.error('Error creating project:', error);
                alert(`Error: ${error.message || 'Failed to create project'}`);
              }
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  {(() => {
                    const text = t('rank_tracking.project_name');
                    return text && text !== 'rank_tracking.project_name' ? text : 'Project Name';
                  })()}
                </label>
                <input
                  type="text"
                  value={rankTrackingModalData.projectName}
                  onChange={(e) => setRankTrackingModalData({ ...rankTrackingModalData, projectName: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="My Website Tracking"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  {(() => {
                    const text = t('rank_tracking.domain');
                    return text && text !== 'rank_tracking.domain' ? text : 'Domain to Track';
                  })()}
                </label>
                <input
                  type="text"
                  value={rankTrackingModalData.domain}
                  onChange={(e) => setRankTrackingModalData({ ...rankTrackingModalData, domain: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="example.com"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">Enter domain without http:// or www</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  {(() => {
                    const text = t('rank_tracking.tracking_frequency');
                    return text && text !== 'rank_tracking.tracking_frequency' ? text : 'Tracking Frequency';
                  })()}
                </label>
                <select
                  value={rankTrackingModalData.trackingFrequency}
                  onChange={(e) => setRankTrackingModalData({ ...rankTrackingModalData, trackingFrequency: e.target.value as 'daily' | 'weekly' | 'monthly' })}
                  className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="daily">{(() => {
                    const text = t('rank_tracking.daily');
                    return text && text !== 'rank_tracking.daily' ? text : 'Daily';
                  })()}</option>
                  <option value="weekly">{(() => {
                    const text = t('rank_tracking.weekly');
                    return text && text !== 'rank_tracking.weekly' ? text : 'Weekly';
                  })()}</option>
                  <option value="monthly">{(() => {
                    const text = t('rank_tracking.monthly');
                    return text && text !== 'rank_tracking.monthly' ? text : 'Monthly';
                  })()}</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowRankTrackingModal(false);
                    setRankTrackingModalData({ projectName: '', domain: '', trackingFrequency: 'daily' });
                  }}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                >
                  Create Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PAYWALL MODAL */}
      {showPaywall && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-black/80 backdrop-blur-sm animate-fadeIn overflow-y-auto">
          <div className="bg-slate-900 border border-indigo-500/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 w-full max-w-6xl my-2 sm:my-4 md:my-8 shadow-2xl relative max-h-[95vh] overflow-y-auto">
             <button onClick={() => setShowPaywall(false)} className="absolute top-2 right-2 sm:top-4 sm:right-4 text-slate-400 hover:text-white z-10 p-2"><X size={20} className="sm:w-6 sm:h-6"/></button>
             
             <div className="text-center mb-4 sm:mb-6 md:mb-8 pr-8 sm:pr-0">
               <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-white mb-2 sm:mb-4">{t('paywall.title')}</h2>
               <p className="text-sm sm:text-base text-slate-400 px-2">{t('paywall.subtitle')}</p>
             </div>

             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                <div className="bg-slate-800 p-4 sm:p-6 rounded-xl border border-slate-700 hover:border-indigo-500 transition-colors flex flex-col">
                   <h3 className="text-lg sm:text-xl font-bold text-white mb-2">{t('paywall.weekly')}</h3>
                   <div className="text-2xl sm:text-3xl font-bold text-indigo-400 my-3 sm:my-4">€19<span className="text-xs sm:text-sm text-slate-500">/wk</span></div>
                   <div className="mt-auto pt-3 sm:pt-4 flex items-center justify-center">
                     <button
                       onClick={() => window.open('https://wa.me/16065577288?text=' + encodeURIComponent('Hi! I would like to subscribe to the Weekly (€19/week) plan. Please send me the payment link.'), '_blank')}
                       className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2"
                     >
                       <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                       Pay with Card
                     </button>
                   </div>
                </div>
                <div className="bg-indigo-900/20 p-4 sm:p-6 rounded-xl border-2 border-indigo-500 relative sm:transform sm:scale-105 flex flex-col">
                   <div className="absolute top-0 right-0 bg-indigo-500 text-white text-xs font-bold px-2 py-1 rounded-bl">{t('paywall.popular')}</div>
                   <h3 className="text-lg sm:text-xl font-bold text-white mb-2">{t('paywall.monthly')}</h3>
                   <div className="text-2xl sm:text-3xl font-bold text-indigo-400 my-3 sm:my-4">€49<span className="text-xs sm:text-sm text-slate-500">/mo</span></div>
                   <div className="mt-auto pt-3 sm:pt-4 flex items-center justify-center">
                     <button
                       onClick={() => window.open('https://wa.me/16065577288?text=' + encodeURIComponent('Hi! I would like to subscribe to the Monthly (€49/month) plan. Please send me the payment link.'), '_blank')}
                       className="w-full bg-white hover:bg-indigo-50 text-indigo-900 py-3 rounded-xl font-bold transition-all shadow-lg flex items-center justify-center gap-2"
                     >
                       <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                       Pay with Card
                     </button>
                   </div>
                </div>
                <div className="bg-slate-800 p-4 sm:p-6 rounded-xl border border-slate-700 hover:border-indigo-500 transition-colors flex flex-col sm:col-span-2 lg:col-span-1">
                   <h3 className="text-lg sm:text-xl font-bold text-white mb-2">{t('paywall.yearly')}</h3>
                   <div className="text-2xl sm:text-3xl font-bold text-indigo-400 my-3 sm:my-4">€399<span className="text-xs sm:text-sm text-slate-500">/yr</span></div>
                   <div className="mt-auto pt-3 sm:pt-4 flex items-center justify-center">
                     <button
                       onClick={() => window.open('https://wa.me/16065577288?text=' + encodeURIComponent('Hi! I would like to subscribe to the Yearly (€399/year) plan. Please send me the payment link.'), '_blank')}
                       className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2"
                     >
                       <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                       Pay with Card
                     </button>
                   </div>
                </div>
             </div>
          </div>
        </div>
      )}

      {/* Email Verification Modal - Priority over language modal */}
      {showVerificationModal && currentUser && pendingVerificationEmail && (
        <EmailVerificationModal
          user={currentUser}
          email={pendingVerificationEmail}
          onVerified={handleEmailVerified}
        />
      )}

      {/* Language Selection Modal (for logged-in users) - Only show if not verifying */}
      {showLanguageModal && currentUser && !showVerificationModal && (
        <LanguageSelectionModal
          isOpen={showLanguageModal}
          onClose={() => setShowLanguageModal(false)}
          onSave={handleLanguageSave}
          currentAnalysisLanguage={currentUser.preferredLanguage || projectConfig.language}
          currentUiLanguage={currentUser.uiLanguage || uiLanguage}
          currentAiAssistantLanguage={currentUser.aiAssistantLanguage || 'Darija/French'}
          currentCountry={currentUser.preferredCountry || projectConfig.country}
        />
      )}

      {/* Language Selection Modal (for landing page) */}
      {showLanguageModalOnLanding && !currentUser && (
        <LanguageSelectionModal
          isOpen={showLanguageModalOnLanding}
          onClose={() => setShowLanguageModalOnLanding(false)}
          onSave={handleLanguageSave}
          currentAnalysisLanguage={localStorage.getItem('orca_analysis_language') || 'English'}
          currentUiLanguage={localStorage.getItem('orca_ui_language') || 'English'}
          currentAiAssistantLanguage={localStorage.getItem('orca_ai_assistant_language') || 'Darija/French'}
          currentCountry={localStorage.getItem('orca_country') || 'United States'}
          showOnLanding={true}
        />
      )}
      
    </div>
  );
};

export default App;